import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import AppModalShell from "../../components/common/AppModalShell";
import AttendanceReviewRequestModal from "../../components/events/AttendanceReviewRequestModal.jsx";
import EventCard from "../../components/events/EventCard";
import { getParticipantRatingGradient } from "../../utils/participantRatingColor";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";
import { buildEventPath } from "../../utils/eventUrl";

const COPY = {
  en: {
    title: "Participation",
    ratingTab: "Participant rating",
    ticketsTab: "My tickets",
    loading: "Loading participant rating...",
    ticketsLoading: "Loading tickets...",
    error: "We could not load your participant rating right now.",
    retry: "Retry",
    historyTitle: "Recent changes",
    historyEmpty: "No rating-changing actions yet.",
    ticketsTitle: "My tickets",
    ticketsEmpty: "No paid event tickets yet.",
    ticketStatusPaid: "Paid",
    ticketStatusRefunded: "Refunded",
    ticketStatusRefundPending: "Refund in review",
    ticketStatusRefundRejected: "Refund rejected",
    ticketPaidTotal: "Paid total",
    ticketBaseAmount: "Ticket amount",
    ticketServiceFee: "Processing fee on this purchase",
    ticketRefunded: "Refunded",
    ticketPaymentsTitle: "Payments",
    ticketRefundsTitle: "Refunds",
    ticketPaymentLabel: "Payment",
    ticketRefundLabel: "Refund",
    ticketPolicyDeduction: "Policy deduction",
    ticketRefundPercent: "Refund policy",
    ticketOpenEvent: "Open event",
    premiumResetTitle: "Premium rating reset",
    manualAttendanceApprovedTitle: "Host approved manual confirmation",
    slotsTitle: "How many events and concepts can you join at once?",
    slotsDescription: "Your participant rating determines your limit for simultaneous joins.",
    activeSlotsTitle: "Your current joins",
    activeSlotsDescription:
      "See the events and concepts you joined as a participant. Tap a card to open its details.",
    activeFlowTitle: "How participation works",
    activeFlowStep1: "Join",
    activeFlowStep2: "Confirm plans",
    activeFlowStep3: "Confirm presence",
    activeFlowStep4: "Rating result",
    activeFlowInfoAlt: "How participation works",
    activeFlowInfoTitle: "How participation works",
    activeStatusJoined: "Joined",
    activeStatusConfirmPlan: "Confirm plans",
    activeStatusPlanConfirmed: "Confirmed",
    activeStatusConfirmPresence: "Confirm presence",
    activeJoinNotCounted: "Does not count toward your limit",
    activeFlowInfoStep1Title: "1. Join the event 💬",
    activeFlowInfoStep1Description:
      "When you join an event, you enter the chat and reserve your spot. If you leave again within 6 hours and you have not confirmed any request yet, your rating does not change.",
    activeFlowInfoStep2Title: "2. Confirm your intention ✅",
    activeFlowInfoStep2Description:
      "During the event flow, the platform may ask whether you still plan to come or whether the updated time still works for you. This helps prevent rating loss just because your plans changed quietly.",
    activeFlowInfoStep3Title: "3. Confirm attendance at the event 📍",
    activeFlowInfoStep3Description:
      "At the event you can confirm attendance by geolocation inside the meetup area or by showing your QR code to the host. After your own attendance is confirmed, you can also help confirm other participants by scanning their QR code.",
    activeFlowInfoStep4Title: "4. Rating result ⭐",
    activeFlowInfoStep4Description:
      "After the event ends, the platform applies the final rating result based on what was confirmed, skipped, or left unanswered during the flow.",
    activeSlotsShowAll: "Show all",
    activeSlotsShowLess: "Show less",
    stepBadgePrefix: "Step",
    slotsUsed: "Joined",
    slotsLeft: "Remaining",
    slotsTotal: "Limit",
    slotsNoLimit: "No limit",
    unlockEntry: "Unlock unlimited events",
    premiumCta: "Get Premium",
    resetEntry: "Reset rating",
    resetCta: "Reset to 100%",
    resetLoading: "Resetting...",
    zeroStateTitle: "Your participant rating is at 0% right now",
    zeroStateDescription:
      "Right now new joins are paused because your participant rating has dropped to zero under the platform rules.",
    zeroStateReasonTitle: "Why joins are paused at 0%",
    zeroStateReasonDescription:
      "This temporary block helps keep the community responsible and reliable for both participants and hosts. When people repeatedly ignore confirmations, leave too late, or do not show up, access to new events is paused.",
    zeroStateRequestCta: "Send request",
    zeroStateRequestLoading: "Sending...",
    zeroStateRequestTitle: "Think this could be a mistake?",
    zeroStateRequestDescription:
      "Send us a request only if you think your recent rating changes were caused by an error. We will review the case and help restore the rating only if there really was a mistake.",
    zeroStateRequestSentTitle: "Request sent",
    zeroStateRequestSentDescription:
      "We received your request and will review whether the recent rating changes were caused by an error.",
    resetReady:
      "Your rating reached 0%. Premium lets you reset it to 100% once per month.",
    resetSuccess: "Participant rating was reset to 100%.",
    resetUsed:
      "Your Premium reset was already used this month. If this looks wrong, contact support.",
    positiveRulesTitle: "Good for your rating ✨",
    neutralRulesTitle: "No change 🤍",
    negativeRulesTitle: "This lowers your rating 🙁",
    slotRulesTitle: "How event slots grow 🧭",
    slotRulesDescription:
      "Your participant rating determines how many events and concepts you can join at the same time.",
    rules: "Rules",
    unlockIntroTitle: "Unlock unlimited events with Premium 💎",
    unlockIntroDescription:
      "Premium unlocks unlimited event slots. Participant rating still stays active, and if it ever falls to 0%, Premium gives you one manual reset to 100% each month.",
    resetIntroTitle: "Reset your rating with Premium 💎",
    resetIntroDescription:
      "Premium gives you unlimited event slots and one manual reset back to 100% each month when your participant rating is 0%.",
    resetManualTitle: "Why Premium reset exists 💛",
    resetManualDescription:
      "An active Premium subscription shows that you seriously want to keep using VaultGrid and keep joining events responsibly. This reset exists for people who want to continue with good intentions, while still protecting the platform from users who repeatedly ignore rules or sabotage events.",
    resetAvailableTitle: "Your reset is ready",
    resetAvailableDescription:
      "Your participant rating reached 0%, so you can use your monthly Premium reset now.",
    resetUsedTitle: "This month's reset is already used",
    resetUsedDescription:
      "Your Premium reset was already used this month. If this looks wrong, contact support.",
    resetLastUsed: "Last used",
    rulesIntroTitle: "Why these rules exist 💛",
    rulesIntroDescription:
      "These rules help keep events reliable and comfortable for both participants and hosts. When people join responsibly and answer requests on time, everyone gets a better experience.",
    closeAlt: "Close participation",
    reviewRequestTitle: "Were you really at the event?",
    reviewRequestDescription:
      "If you really attended the event but did not confirm your attendance for some reason, you can ask the host for manual confirmation.",
    reviewRequestPenalty:
      "If the host approves it, your rating will go down by only 10% instead of 25%.",
    reviewRequestHostNote: "",
    reviewRequestPhotoHint:
      "Please make sure your profile photo is easy to recognize so the host can identify you.",
    reviewRequestCta: "Send request to host",
    reviewRequestSending: "Sending...",
    reviewRequestCancel: "Cancel",
    reviewRequestPending: "Request sent to host",
    reviewRequestRejected: "Host did not approve your request for manual confirmation",
    reviewRequestApproved: "Host approved manual confirmation",
    changeReasonTitle: "Reason for this change",
    showReasonAlt: "Show reason",
    hideReasonAlt: "Hide reason",
    manualReviewAdjustedReasonTitle: "Host approved manual confirmation",
    manualReviewAdjustedReasonDescription:
      "The host manually confirmed that you were at the event, so your rating went down by only 10% instead of 25%.",
  },
  ru: {
    title: "Участие",
    ratingTab: "Рейтинг участника",
    ticketsTab: "Мои билеты",
    loading: "Загружаем рейтинг участника...",
    ticketsLoading: "Загружаем билеты...",
    error: "Сейчас не удалось загрузить рейтинг участника.",
    retry: "Повторить",
    historyTitle: "Последние изменения",
    historyEmpty: "Пока нет действий, которые повлияли на рейтинг.",
    ticketsTitle: "Мои билеты",
    ticketsEmpty: "Пока нет оплаченных билетов на события.",
    ticketStatusPaid: "Оплачен",
    ticketStatusRefunded: "Возврат оформлен",
    ticketStatusRefundPending: "Возврат на проверке",
    ticketStatusRefundRejected: "Возврат отклонён",
    ticketPaidTotal: "Оплачено всего",
    ticketBaseAmount: "Сумма билета",
    ticketServiceFee: "Сбор за обработку по этой покупке",
    ticketRefunded: "Возвращено",
    ticketPaymentsTitle: "Оплаты",
    ticketRefundsTitle: "Возвраты",
    ticketPaymentLabel: "Оплата",
    ticketRefundLabel: "Возврат",
    ticketPolicyDeduction: "Удержание по правилам",
    ticketRefundPercent: "Политика возврата",
    ticketOpenEvent: "Открыть событие",
    premiumResetTitle: "Premium-сброс рейтинга",
    manualAttendanceApprovedTitle: "Хост одобрил ручное подтверждение",
    slotsTitle: "На сколько событий и концептов можно записаться одновременно?",
    slotsDescription: "Рейтинг участника определяет лимит одновременных записей.",
    activeSlotsTitle: "Ваши текущие записи",
    activeSlotsDescription:
      "Здесь события и концепты, к которым вы присоединились как участник. Нажмите на карточку, чтобы открыть подробности.",
    activeFlowTitle: "Как проходит участие",
    activeFlowStep1: "Запись",
    activeFlowStep2: "Подтверждение планов",
    activeFlowStep3: "Подтверждение присутствия",
    activeFlowStep4: "Итог для рейтинга",
    activeFlowInfoAlt: "Как проходит участие",
    activeFlowInfoTitle: "Как проходит участие",
    activeStatusJoined: "Вы записаны",
    activeStatusConfirmPlan: "Подтвердите планы",
    activeStatusPlanConfirmed: "Подтверждено",
    activeStatusConfirmPresence: "Подтвердите участие",
    activeJoinNotCounted: "Не учитывается в лимите",
    activeFlowInfoStep1Title: "1. Подключение к событию 💬",
    activeFlowInfoStep1Description:
      "Когда вы присоединяетесь к ивенту, вы попадаете в чат и резервируете своё место. Если вы выйдете обратно в течение 6 часов и ещё не подтверждали никакой запрос, рейтинг не изменится.",
    activeFlowInfoStep2Title: "2. Подтверждение намерения ✅",
    activeFlowInfoStep2Description:
      "По ходу подготовки ивента платформа может попросить подтвердить, что вы всё ещё планируете прийти, или что вам всё ещё подходит обновлённое время. Это сделано для того, чтобы рейтинг не снижался просто из-за тихо изменившихся планов.",
    activeFlowInfoStep3Title: "3. Подтверждение участия на месте 📍",
    activeFlowInfoStep3Description:
      "На самом мероприятии вы можете подтвердить присутствие через геолокацию в зоне сбора или показав QR-код хосту. После своего подтверждения вы также можете помочь другим участникам, отсканировав их QR-код.",
    activeFlowInfoStep4Title: "4. Результат для рейтинга ⭐",
    activeFlowInfoStep4Description:
      "После завершения ивента платформа применяет итоговое изменение рейтинга в зависимости от того, что было подтверждено, пропущено или оставлено без ответа.",
    activeSlotsShowAll: "Показать все",
    activeSlotsShowLess: "Скрыть",
    stepBadgePrefix: "Шаг",
    slotsUsed: "Записи",
    slotsLeft: "Осталось",
    slotsTotal: "Лимит",
    slotsNoLimit: "Без лимита",
    unlockEntry: "Открыть безлимитные ивенты",
    premiumCta: "Открыть Premium",
    resetEntry: "Сброс рейтинга",
    resetCta: "Сбросить до 100%",
    resetLoading: "Сбрасываем...",
    zeroStateTitle: "Ваш рейтинг участника сейчас на 0%",
    zeroStateDescription:
      "Сейчас новые подключения временно остановлены, потому что по правилам платформы ваш рейтинг участника снизился до нуля.",
    zeroStateReasonTitle: "Почему при 0% подключения ставятся на паузу",
    zeroStateReasonDescription:
      "Эта временная блокировка нужна, чтобы комьюнити оставалось ответственным и надёжным и для участников, и для хостов. Если человек регулярно игнорирует подтверждения, выходит слишком поздно или не приходит, доступ к новым ивентам временно останавливается.",
    zeroStateRequestCta: "Отправить запрос",
    zeroStateRequestLoading: "Отправляем...",
    zeroStateRequestTitle: "Думаете, что это ошибка?",
    zeroStateRequestDescription:
      "Отправьте нам запрос только если вы считаете, что последние изменения рейтинга произошли по ошибке. Мы проверим кейс и поможем восстановить рейтинг только если действительно была ошибка.",
    zeroStateRequestSentTitle: "Запрос отправлен",
    zeroStateRequestSentDescription:
      "Мы получили ваш запрос и проверим, были ли последние изменения рейтинга ошибочными.",
    resetReady:
      "Ваш рейтинг достиг 0%. В Premium его можно сбросить до 100% один раз в месяц.",
    resetSuccess: "Рейтинг участника был сброшен до 100%.",
    resetUsed:
      "В этом месяце Premium-сброс уже был использован. Если это ошибка, свяжитесь с поддержкой.",
    positiveRulesTitle: "Хорошо для рейтинга ✨",
    neutralRulesTitle: "Без изменений 🤍",
    negativeRulesTitle: "Это понижает рейтинг 🙁",
    slotRulesTitle: "Как растут слоты на ивенты 🧭",
    slotRulesDescription:
      "Рейтинг участника определяет, на сколько мероприятий и концептов можно записаться одновременно.",
    rules: "Правила",
    unlockIntroTitle: "Откройте безлимитные ивенты с Premium 💎",
    unlockIntroDescription:
      "Premium открывает неограниченное количество слотов на ивенты. Рейтинг участника всё равно остаётся активным, и если он когда-нибудь упадёт до 0%, в Premium у вас будет один ручной сброс до 100% в месяц.",
    resetIntroTitle: "Сбросьте рейтинг через Premium 💎",
    resetIntroDescription:
      "Premium даёт неограниченные слоты на ивенты и один ручной сброс рейтинга обратно до 100% каждый месяц, когда рейтинг участника находится на 0%.",
    resetManualTitle: "Почему сброс через Premium вообще существует 💛",
    resetManualDescription:
      "Активная Premium-подписка показывает, что человек действительно серьёзно настроен продолжать пользоваться VaultGrid и ответственно ходить на мероприятия. Такой сброс нужен для тех, кто хочет вернуться к нормальному участию, и при этом помогает отсеивать пользователей, которые системно игнорируют правила или срывают события.",
    resetAvailableTitle: "Сброс уже доступен",
    resetAvailableDescription:
      "Ваш рейтинг участника достиг 0%, поэтому вы можете использовать свой месячный Premium-сброс прямо сейчас.",
    resetUsedTitle: "Сброс за этот месяц уже использован",
    resetUsedDescription:
      "В этом месяце ваш Premium-сброс уже был использован. Если это выглядит ошибкой, свяжитесь с поддержкой.",
    resetLastUsed: "Последний сброс",
    rulesIntroTitle: "Зачем нужны эти правила 💛",
    rulesIntroDescription:
      "Эти правила помогают сделать опыт и для участников, и для хостов более надёжным и комфортным. Когда люди ответственно записываются и вовремя отвечают на запросы, всем становится проще и приятнее.",
    closeAlt: "Закрыть участие",
    reviewRequestTitle: "Вы действительно были на мероприятии?",
    reviewRequestDescription:
      "Если вы действительно были на мероприятии, но по какой-то причине не подтвердили присутствие, вы можете попросить организатора вручную подтвердить ваше участие.",
    reviewRequestPenalty:
      "Если организатор одобрит запрос, ваш рейтинг снизится только на 10% вместо 25%.",
    reviewRequestHostNote: "",
    reviewRequestPhotoHint:
      "Пожалуйста, убедитесь, что на вашем профиле стоит хорошо различимая фотография, чтобы организатор мог вас узнать.",
    reviewRequestCta: "Отправить запрос организатору",
    reviewRequestSending: "Отправляем...",
    reviewRequestCancel: "Отмена",
    reviewRequestPending: "Запрос отправлен организатору",
    reviewRequestRejected: "Организатор не одобрил ваш запрос на ручное подтверждение",
    reviewRequestApproved: "Организатор одобрил ручное подтверждение",
    changeReasonTitle: "Причина этого изменения",
    showReasonAlt: "Показать причину",
    hideReasonAlt: "Скрыть причину",
    manualReviewAdjustedReasonTitle: "Организатор одобрил ручное подтверждение",
    manualReviewAdjustedReasonDescription:
      "Организатор вручную подтвердил, что вы были на мероприятии, поэтому ваш рейтинг снизился только на 10% вместо 25%.",
  },
  sk: {
    title: "Účasť",
    ratingTab: "Rating účastníka",
    ticketsTab: "Moje lístky",
    loading: "Načítavam účastnícky rating...",
    ticketsLoading: "Načítavam lístky...",
    error: "Participant rating sa teraz nepodarilo načítať.",
    retry: "Skúsiť znova",
    historyTitle: "Posledné zmeny",
    historyEmpty: "Zatiaľ tu nie sú žiadne akcie, ktoré zmenili rating.",
    ticketsTitle: "Moje lístky",
    ticketsEmpty: "Zatiaľ tu nie sú žiadne zaplatené lístky na udalosti.",
    ticketStatusPaid: "Zaplatené",
    ticketStatusRefunded: "Refund odoslaný",
    ticketStatusRefundPending: "Refund v kontrole",
    ticketStatusRefundRejected: "Refund zamietnutý",
    ticketPaidTotal: "Zaplatené spolu",
    ticketBaseAmount: "Suma lístka",
    ticketServiceFee: "Poplatok za spracovanie pri tomto nákupe",
    ticketRefunded: "Vrátené",
    ticketPaymentsTitle: "Platby",
    ticketRefundsTitle: "Refundy",
    ticketPaymentLabel: "Platba",
    ticketRefundLabel: "Refund",
    ticketPolicyDeduction: "Zrážka podľa pravidiel",
    ticketRefundPercent: "Refund policy",
    ticketOpenEvent: "Otvoriť udalosť",
    premiumResetTitle: "Obnovenie ratingu cez Premium",
    manualAttendanceApprovedTitle: "Hostiteľ schválil manuálne potvrdenie",
    slotsTitle: "Na koľko udalostí a konceptov sa môžeš prihlásiť naraz?",
    slotsDescription: "Počet súčasných prihlásení závisí od tvojho ratingu účastníka.",
    activeSlotsTitle: "Tvoje aktuálne prihlásenia",
    activeSlotsDescription:
      "Tu nájdeš udalosti a koncepty, ku ktorým si sa pridal ako účastník. Ťuknutím otvoríš detail.",
    activeFlowTitle: "Ako prebieha účasť",
    activeFlowStep1: "Prihlásenie",
    activeFlowStep2: "Potvrdenie plánu",
    activeFlowStep3: "Potvrdenie prítomnosti",
    activeFlowStep4: "Vyhodnotenie",
    activeFlowInfoAlt: "Ako prebieha účasť",
    activeFlowInfoTitle: "Ako prebieha účasť",
    activeStatusJoined: "Prihlásené",
    activeStatusConfirmPlan: "Potvrď plán",
    activeStatusPlanConfirmed: "Potvrdené",
    activeStatusConfirmPresence: "Potvrď účasť",
    activeJoinNotCounted: "Nezapočítava sa do limitu",
    activeFlowInfoStep1Title: "1. Pripojenie k udalosti 💬",
    activeFlowInfoStep1Description:
      "Keď sa pripojíš k udalosti, dostaneš sa do chatu a rezervuješ si miesto. Ak odídeš späť do 6 hodín a ešte si nepotvrdil žiadnu výzvu, rating sa ti nezmení.",
    activeFlowInfoStep2Title: "2. Potvrdenie záujmu ✅",
    activeFlowInfoStep2Description:
      "Počas prípravy udalosti sa ťa platforma môže opýtať, či stále plánuješ prísť alebo či ti po presune stále vyhovuje čas. Je to preto, aby sa rating neznižoval len kvôli tomu, že sa tvoje plány ticho zmenili.",
    activeFlowInfoStep3Title: "3. Potvrdenie účasti na mieste 📍",
    activeFlowInfoStep3Description:
      "Na samotnej udalosti môžeš potvrdiť účasť cez geolokáciu v zóne stretnutia alebo ukázaním QR kódu hostiteľovi. Po vlastnom potvrdení môžeš pomôcť potvrdiť aj ostatných participantov naskenovaním ich QR kódu.",
    activeFlowInfoStep4Title: "4. Výsledok ratingu ⭐",
    activeFlowInfoStep4Description:
      "Po skončení udalosti platforma zapíše finálny výsledok ratingu podľa toho, čo bolo potvrdené, zmeškané alebo ponechané bez odpovede.",
    activeSlotsShowAll: "Zobraziť všetko",
    activeSlotsShowLess: "Skryť",
    stepBadgePrefix: "Krok",
    slotsUsed: "Prihlásenia",
    slotsLeft: "Zostáva",
    slotsTotal: "Limit",
    slotsNoLimit: "Bez limitu",
    unlockEntry: "Odomknúť neobmedzené udalosti",
    premiumCta: "Získať Premium",
    resetEntry: "Reset ratingu",
    resetCta: "Resetnúť na 100 %",
    resetLoading: "Resetujem...",
    zeroStateTitle: "Tvoj účastnícky rating je teraz na 0 %",
    zeroStateDescription:
      "Nové prihlasovanie je teraz dočasne pozastavené, pretože tvoj účastnícky rating podľa pravidiel platformy klesol na nulu.",
    zeroStateReasonTitle: "Prečo sa pri 0 % nové pridania pozastavia",
    zeroStateReasonDescription:
      "Táto dočasná blokácia pomáha udržať komunitu zodpovednú a spoľahlivú pre participantov aj hostiteľov. Ak niekto opakovane ignoruje potvrdenia, odchádza príliš neskoro alebo nepríde, prístup k novým udalostiam sa dočasne zastaví.",
    zeroStateRequestCta: "Poslať request",
    zeroStateRequestLoading: "Posielam...",
    zeroStateRequestTitle: "Myslíš si, že je to chyba?",
    zeroStateRequestDescription:
      "Pošli nám request len vtedy, ak si myslíš, že posledné zmeny ratingu vznikli chybou. Prípad skontrolujeme a rating pomôžeme obnoviť iba ak naozaj išlo o chybu.",
    zeroStateRequestSentTitle: "Request bol odoslaný",
    zeroStateRequestSentDescription:
      "Tvoj request sme prijali a preveríme, či posledné zmeny ratingu nevznikli chybou.",
    resetReady:
      "Tvoj rating dosiahol 0 %. V Premium si ho môžeš resetnúť na 100 % raz za mesiac.",
    resetSuccess: "Participant rating bol resetnutý na 100 %.",
    resetUsed:
      "Premium reset si už tento mesiac použil. Ak je to chyba, kontaktuj support.",
    positiveRulesTitle: "Čo posilňuje tvoj rating ✨",
    neutralRulesTitle: "Bez zmeny 🤍",
    negativeRulesTitle: "Čo znižuje tvoj rating 🙁",
    slotRulesTitle: "Ako sa zvyšujú miesta na udalosti 🧭",
    slotRulesDescription:
      "Tvoj rating určuje, na koľko udalostí a konceptov sa môžeš prihlásiť naraz.",
    rules: "Pravidlá",
    unlockIntroTitle: "Odomkni neobmedzené udalosti s Premium 💎",
    unlockIntroDescription:
      "Premium odomyká neobmedzené sloty na udalosti. Participant rating zostáva aktívny a ak niekedy klesne na 0 %, v Premium získaš jeden manuálny reset späť na 100 % každý mesiac.",
    resetIntroTitle: "Resetni svoj rating cez Premium 💎",
    resetIntroDescription:
      "Premium ti dá neobmedzené sloty na udalosti a jeden manuálny reset ratingu späť na 100 % každý mesiac, keď je participant rating na 0 %.",
    resetManualTitle: "Prečo Premium reset vôbec existuje 💛",
    resetManualDescription:
      "Aktívne Premium ukazuje, že to s VaultGrid myslíš vážne a chceš pokračovať zodpovedne aj naďalej chodiť na udalosti. Tento reset je pre ľudí, ktorí chcú normálne pokračovať, a zároveň pomáha odfiltrovať používateľov, ktorí opakovane ignorujú pravidlá alebo narúšajú udalosti.",
    resetAvailableTitle: "Tvoj reset je pripravený",
    resetAvailableDescription:
      "Tvoj participant rating dosiahol 0 %, takže svoj mesačný Premium reset môžeš použiť hneď teraz.",
    resetUsedTitle: "Reset na tento mesiac je už použitý",
    resetUsedDescription:
      "Tvoj Premium reset si už tento mesiac použil. Ak to vyzerá nesprávne, kontaktuj support.",
    resetLastUsed: "Naposledy použité",
    rulesIntroTitle: "Prečo tieto pravidlá existujú 💛",
    rulesIntroDescription:
      "Tieto pravidlá pomáhajú udržať udalosti spoľahlivé a príjemné pre participantov aj hostiteľov. Keď ľudia reagujú zodpovedne a potvrdzujú včas, všetci majú lepší zážitok.",
    closeAlt: "Zavrieť účasť",
    reviewRequestTitle: "Bol(a) si na udalosti naozaj?",
    reviewRequestDescription:
      "Ak si na udalosti naozaj bol(a), ale z nejakého dôvodu si nepotvrdil(a) účasť, môžeš požiadať hostiteľa o manuálne potvrdenie.",
    reviewRequestPenalty:
      "Ak to hostiteľ schváli, tvoj rating sa zníži len o 10 % namiesto 25 %.",
    reviewRequestHostNote: "",
    reviewRequestPhotoHint:
      "Prosím uisti sa, že na profile máš dobre rozpoznateľnú fotografiu, aby ťa hostiteľ vedel identifikovať.",
    reviewRequestCta: "Poslať žiadosť hostiteľovi",
    reviewRequestSending: "Posielam...",
    reviewRequestCancel: "Zrušiť",
    reviewRequestPending: "Žiadosť bola odoslaná hostiteľovi",
    reviewRequestRejected: "Hostiteľ neschválil tvoju žiadosť o manuálne potvrdenie",
    reviewRequestApproved: "Hostiteľ schválil manuálne potvrdenie",
    changeReasonTitle: "Dôvod tejto zmeny",
    showReasonAlt: "Zobraziť dôvod",
    hideReasonAlt: "Skryť dôvod",
    manualReviewAdjustedReasonTitle: "Hostiteľ schválil manuálne potvrdenie",
    manualReviewAdjustedReasonDescription:
      "Hostiteľ manuálne potvrdil, že si na udalosti naozaj bol(a), preto sa tvoj rating znížil len o 10 % namiesto 25 %.",
  },
  cs: {
    ratingTab: "Hodnocení účastníka",
    slotsTitle: "Na kolik událostí a konceptů se můžeš přihlásit najednou?",
    slotsDescription: "Počet souběžných přihlášení závisí na tvém hodnocení účastníka.",
    activeSlotsTitle: "Tvoje aktuální přihlášení",
    activeSlotsDescription:
      "Tady najdeš události a koncepty, ke kterým ses přidal jako účastník. Klepnutím otevřeš detail.",
    activeFlowTitle: "Jak probíhá účast",
    activeFlowStep1: "Přihlášení",
    activeFlowStep2: "Potvrzení plánu",
    activeFlowStep3: "Potvrzení přítomnosti",
    activeFlowStep4: "Vyhodnocení",
    activeFlowInfoAlt: "Jak probíhá účast",
    activeFlowInfoTitle: "Jak probíhá účast",
    activeStatusJoined: "Přihlášeno",
    activeStatusConfirmPlan: "Potvrď plán",
    activeStatusPlanConfirmed: "Potvrzeno",
    activeStatusConfirmPresence: "Potvrď účast",
    activeJoinNotCounted: "Nezapočítává se do limitu",
    slotsUsed: "Přihlášení",
    slotsLeft: "Zbývá",
    slotsTotal: "Limit",
    slotsNoLimit: "Bez limitu",
  },
};

// Keep the currently deployed policy text until the backend advertises v3.
// Mobile builds can therefore ship before the coordinated backend release.
const COPY_V3_OVERRIDES = {
  en: {
    activeFlowInfoStep1Description: "Joining reserves a spot. If the event starts within 24 hours, you have 15 minutes to undo an accidental join; otherwise you have 6 hours, provided you have not confirmed a request.",
    resetCta: "Reset to 100%",
    resetReady: "After your rating reaches 0%, Premium can reset it to 100% immediately, once per month.",
    resetSuccess: "Participant rating was reset to 100%.",
    zeroStateDescription: "New event joins are paused. After seven days, your rating will recover to 10% with one slot.",
    zeroStateReasonDescription: "A missed event or late cancellation can bring your rating to zero. After seven days you can join one event and rebuild your rating by attending.",
    recoveredTitle: "Your rating is recovering",
    zeroRecoveryTitle: "One event slot restored",
    recoveredDescription: "You have one event slot at 10%. Attend events to rebuild your rating, or use an available Premium reset to reach 100%.",
    cooldownInProgressDescription: "Your rating has increased, but new joins stay paused for seven days after reaching 0%.",
    cooldownUntil: "One event slot returns on",
    unlockIntroDescription: "Premium unlocks unlimited event slots. If your rating reaches 0%, you can reset it to 100% immediately, once per month.",
    resetIntroDescription: "After your rating reaches 0%, Premium lets you reset it to 100% immediately, once per month.",
    resetAvailableDescription: "Your monthly Premium reset is available. It restores your rating to 100%.",
    reviewRequestPenalty: "If the host approves it, your rating will go down by only 10 points instead of 30.",
    manualReviewAdjustedReasonDescription: "The host confirmed your attendance. The adjusted rating change is shown on this card.",
  },
  ru: {
    activeFlowInfoStep1Description: "Подключение резервирует место. Если до начала меньше 24 часов, ошибочную запись можно отменить без штрафа в течение 15 минут; иначе действует срок 6 часов, если вы ещё не подтвердили запрос.",
    resetCta: "Сбросить до 100%",
    resetReady: "После падения рейтинга до 0% Premium позволяет сразу поднять его до 100% один раз в месяц.",
    resetSuccess: "Рейтинг участника поднят до 100%.",
    zeroStateDescription: "Новые записи временно закрыты. Через семь дней рейтинг восстановится до 10% и появится один слот.",
    zeroStateReasonDescription: "Неявка или поздняя отмена могут снизить рейтинг до нуля. Через семь дней можно будет записаться на один ивент и восстановить рейтинг посещениями.",
    recoveredTitle: "Рейтинг восстанавливается",
    zeroRecoveryTitle: "Один слот восстановлен",
    recoveredDescription: "При 10% доступен один слот. Посещайте ивенты, чтобы восстановить рейтинг, или используйте доступный Premium-сброс до 100%.",
    cooldownInProgressDescription: "Рейтинг вырос, но новые записи остаются закрытыми семь дней после падения до 0%.",
    cooldownUntil: "Один слот вернётся",
    unlockIntroDescription: "Premium открывает неограниченные слоты. После падения рейтинга до 0% его можно сразу поднять до 100% один раз в месяц.",
    resetIntroDescription: "После падения рейтинга до 0% Premium позволяет сразу поднять его до 100% один раз в месяц.",
    resetAvailableDescription: "Ежемесячный Premium-сброс доступен и поднимет рейтинг до 100%.",
    reviewRequestPenalty: "Если организатор одобрит запрос, рейтинг снизится только на 10 пунктов вместо 30.",
    manualReviewAdjustedReasonDescription: "Организатор подтвердил присутствие. Скорректированное изменение рейтинга показано на карточке.",
  },
  sk: {
    activeFlowInfoStep1Description: "Pripojenie rezervuje miesto. Ak sa udalosť začne do 24 hodín, omyl môžeš bez sankcie opraviť do 15 minút; inak máš 6 hodín, pokiaľ si ešte nepotvrdil žiadosť.",
    resetCta: "Resetnúť na 100 %",
    resetReady: "Keď rating klesne na 0 %, Premium ho umožní hneď obnoviť na 100 %, raz za mesiac.",
    resetSuccess: "Participant rating bol obnovený na 100 %.",
    zeroStateDescription: "Nové pripojenia sú pozastavené. Po siedmich dňoch sa rating obnoví na 10 % a získaš jedno miesto.",
    zeroStateReasonDescription: "Neúčasť alebo neskorý odchod môžu znížiť rating na nulu. Po siedmich dňoch sa môžeš pripojiť k jednej udalosti a obnovovať rating účasťou.",
    recoveredTitle: "Tvoj rating sa obnovuje",
    zeroRecoveryTitle: "Jedno miesto bolo obnovené",
    recoveredDescription: "Pri 10 % máš jedno miesto. Rating zvýšiš účasťou, alebo môžeš použiť dostupný Premium reset na 100 %.",
    cooldownInProgressDescription: "Rating sa zvýšil, ale nové pripojenia zostávajú pozastavené sedem dní po poklese na 0 %.",
    cooldownUntil: "Jedno miesto sa vráti",
    unlockIntroDescription: "Premium odomyká neobmedzené miesta. Keď rating klesne na 0 %, môžeš ho hneď obnoviť na 100 %, raz za mesiac.",
    resetIntroDescription: "Keď rating klesne na 0 %, Premium ho umožní hneď obnoviť na 100 %, raz za mesiac.",
    resetAvailableDescription: "Mesačný Premium reset je dostupný a obnoví rating na 100 %.",
    reviewRequestPenalty: "Ak to hostiteľ schváli, rating klesne len o 10 bodov namiesto 30.",
    manualReviewAdjustedReasonDescription: "Hostiteľ potvrdil tvoju účasť. Upravená zmena ratingu je zobrazená na karte.",
  },
};

const RULE_COPY = {
  en: {
    introTitle: "Why these rules exist 💛",
    introDescription:
      "These rules help keep events reliable and comfortable for both participants and hosts. When people join responsibly and answer requests on time, everyone gets a better experience.",
    attended_event: {
      title: "✅ You came and confirmed attendance",
      description: "You came and confirmed attendance for the event.",
    },
    fast_confirmation: {
      title: "⚡ You quickly confirmed the request",
      description: "You quickly answered the participation request.",
    },
    missed_event: {
      title: "😕 You did not come",
      description: "You were in the event but did not come, or your attendance was not confirmed.",
    },
    left_under_6h: {
      title: "⏰ You left less than 6 hours before the event",
      description: "You left very close to the start.",
    },
    request_timeout: {
      title: "📩 You did not confirm the request",
      description: "You ignored the confirmation request even after the reminder.",
    },
    left_under_24h: {
      title: "🕒 You left less than 24 hours before the event",
      description: "You left too close to the start.",
    },
    confirmed_leave: {
      title: "↩️ You left after confirming participation",
      description: "You confirmed that you are coming and later left the event.",
    },
    confirmed_leave_under_12h: {
      title: "↩️ You left after confirming less than 12 hours before the event",
      description: "You confirmed participation and left too close to the start.",
    },
    confirmed_leave_under_6h: {
      title: "↩️ You left after confirming less than 6 hours before the event",
      description: "You confirmed participation and left almost right before the start.",
    },
    leave_after_grace: {
      title: "🚪 You left after staying in the event for more than 6 hours",
      description: "You left late after already occupying a participant slot.",
    },
  },
  ru: {
    introTitle: "Зачем нужны эти правила 💛",
    introDescription:
      "Эти правила помогают сделать опыт и для участников, и для хостов более надёжным и комфортным. Когда люди ответственно записываются и вовремя отвечают на запросы, всем становится проще и приятнее.",
    attended_event: {
      title: "✅ Вы пришли и подтвердили участие",
      description: "Вы пришли и подтвердили участие в мероприятии.",
    },
    fast_confirmation: {
      title: "⚡ Вы быстро подтвердили запрос",
      description: "Вы быстро ответили на запрос об участии.",
    },
    missed_event: {
      title: "😕 Вы не пришли",
      description: "Вы были записаны на мероприятие, но не пришли, или ваше присутствие не было подтверждено.",
    },
    left_under_6h: {
      title: "⏰ Вы вышли менее чем за 6 часов до мероприятия",
      description: "Вы вышли слишком близко к началу.",
    },
    request_timeout: {
      title: "📩 Вы не подтвердили запрос",
      description: "Вы проигнорировали запрос на подтверждение даже после напоминания.",
    },
    left_under_24h: {
      title: "🕒 Вы вышли менее чем за 24 часа до мероприятия",
      description: "Вы вышли слишком близко к началу.",
    },
    confirmed_leave: {
      title: "↩️ Вы вышли после подтверждения участия",
      description: "Вы подтвердили, что придёте, а потом вышли из мероприятия.",
    },
    confirmed_leave_under_12h: {
      title: "↩️ Вы вышли после подтверждения менее чем за 12 часов до мероприятия",
      description: "Вы подтвердили участие, но вышли слишком близко к началу.",
    },
    confirmed_leave_under_6h: {
      title: "↩️ Вы вышли после подтверждения менее чем за 6 часов до мероприятия",
      description: "Вы подтвердили участие, но вышли почти перед самым стартом.",
    },
    leave_after_grace: {
      title: "🚪 Вы вышли после того, как были в мероприятии больше 6 часов",
      description: "Вы поздно вышли, уже занимая слот участника.",
    },
  },
  sk: {
    introTitle: "Prečo tieto pravidlá existujú 💛",
    introDescription:
      "Tieto pravidlá pomáhajú udržiavať eventy spoľahlivé pre účastníkov a hostiteľov. Keď ľudia reagujú zodpovedne a potvrdzujú včas, všetci majú lepší zážitok.",
    attended_event: {
      title: "✅ Prišiel si a potvrdil účasť",
      description: "Prišiel si a potvrdil účasť na udalosti.",
    },
    fast_confirmation: {
      title: "⚡ Rýchlo si potvrdil záujem",
      description: "Rýchlo si odpovedal na žiadosť o účasť.",
    },
    missed_event: {
      title: "😕 Neprišiel si",
      description: "Bol si v udalosti, ale neprišiel si, alebo tvoja účasť nebola potvrdená.",
    },
    left_under_6h: {
      title: "⏰ Odišiel si menej ako 6 hodín pred udalosťou",
      description: "Odišiel si príliš krátko pred začiatkom.",
    },
    request_timeout: {
      title: "📩 Nepotvrdil si záujem zúčastniť sa",
      description: "Ignoroval si výzvu na potvrdenie aj po pripomenutí.",
    },
    left_under_24h: {
      title: "🕒 Odišiel si menej ako 24 hodín pred udalosťou",
      description: "Odišiel si príliš krátko pred začiatkom.",
    },
    confirmed_leave: {
      title: "↩️ Odišiel si po potvrdení účasti",
      description: "Potvrdil si, že prídeš, a potom si z udalosti odišiel.",
    },
    confirmed_leave_under_12h: {
      title: "↩️ Odišiel si po potvrdení menej ako 12 hodín pred udalosťou",
      description: "Potvrdil si účasť, ale odišiel si príliš krátko pred začiatkom.",
    },
    confirmed_leave_under_6h: {
      title: "↩️ Odišiel si po potvrdení menej ako 6 hodín pred udalosťou",
      description: "Potvrdil si účasť, ale odišiel si takmer tesne pred začiatkom.",
    },
    leave_after_grace: {
      title: "🚪 Odišiel si po viac ako 6 hodinách v udalosti",
      description: "Odišiel si neskoro po tom, čo si už zaberal participant slot.",
    },
  },
};

const RULE_COPY_V3 = {
  en: {
    ...RULE_COPY.en,
    fast_confirmation: { title: "⚡ Quick reply and attendance", description: "A quick reply earns 5 points only after you attend; once per event." },
    early_leave: { title: "Early cancellation", description: "No penalty if you leave more than 24 hours before the event without confirming participation." },
    join_grace: { title: "Accidental join", description: "Within 24 hours of the start, you have 15 minutes to undo an unconfirmed join. Earlier joins have six hours." },
    host_change_waiver: { title: "The host changed the event", description: "A recent schedule change or changed location can waive the penalty for existing participants." },
    zero_recovery: { title: "Recovery from 0%", description: "After seven days, your rating rises to 10% and you regain one slot. An unused Premium reset can restore it to 100% immediately after it reaches 0%." },
    left_under_24h: { title: "🕒 Left 6–24 hours before", description: "Cancellation without confirmation 6–24 hours before the event." },
    left_under_6h: { title: "⏰ Left less than 6 hours before", description: "Cancellation without confirmation very close to the start." },
    confirmed_leave: { title: "↩️ Left after confirming, more than 24 hours before", description: "You confirmed participation and then cancelled early." },
    confirmed_leave_under_24h: { title: "↩️ Left 12–24 hours before after confirming", description: "You confirmed participation and cancelled 12–24 hours before the start." },
    confirmed_leave_under_12h: { title: "↩️ Left 6–12 hours before after confirming", description: "You confirmed participation and cancelled 6–12 hours before the start." },
    confirmed_leave_under_6h: { title: "↩️ Left less than 6 hours before after confirming", description: "You confirmed participation and cancelled just before the start." },
  },
  ru: {
    ...RULE_COPY.ru,
    fast_confirmation: { title: "⚡ Быстрый ответ и посещение", description: "Быстрый ответ даст 5 пунктов только после посещения, один раз за ивент." },
    early_leave: { title: "Ранняя отмена", description: "Без штрафа: выход более чем за 24 часа без подтверждённого участия." },
    join_grace: { title: "Ошибочная запись", description: "Если до начала меньше 24 часов, выход без подтверждения бесплатный первые 15 минут. При ранней записи — первые 6 часов." },
    host_change_waiver: { title: "Организатор изменил ивент", description: "Недавнее изменение времени или места может отменить штраф для уже записанных участников." },
    zero_recovery: { title: "Восстановление после 0%", description: "Через семь дней рейтинг станет 10% и вернётся один слот. Неиспользованный Premium-сброс позволяет поднять рейтинг до 100% сразу после падения до 0%." },
    left_under_24h: { title: "🕒 Выход за 6–24 часа", description: "Отмена за 6–24 часа без подтверждённого участия." },
    left_under_6h: { title: "⏰ Выход менее чем за 6 часов", description: "Отмена без подтверждения почти перед началом." },
    confirmed_leave: { title: "↩️ Выход после подтверждения более чем за 24 часа", description: "Вы подтвердили участие, а затем отменили его заранее." },
    confirmed_leave_under_24h: { title: "↩️ Выход за 12–24 часа после подтверждения", description: "Вы подтвердили участие и отменили его за 12–24 часа." },
    confirmed_leave_under_12h: { title: "↩️ Выход за 6–12 часов после подтверждения", description: "Вы подтвердили участие и отменили его за 6–12 часов." },
    confirmed_leave_under_6h: { title: "↩️ Выход менее чем за 6 часов после подтверждения", description: "Вы подтвердили участие и отменили его почти перед началом." },
  },
  sk: {
    ...RULE_COPY.sk,
    fast_confirmation: { title: "⚡ Rýchla odpoveď a účasť", description: "Rýchla odpoveď pridá 5 bodov až po účasti, raz za udalosť." },
    early_leave: { title: "Skorý odchod", description: "Bez sankcie pri odchode viac ako 24 hodín vopred bez potvrdenej účasti." },
    join_grace: { title: "Omyl pri pripojení", description: "Ak do začiatku ostáva menej než 24 hodín, nepotvrdené pripojenie môžeš zrušiť do 15 minút. Inak do šiestich hodín." },
    host_change_waiver: { title: "Hostiteľ zmenil udalosť", description: "Nedávna zmena termínu alebo miesta môže zrušiť sankciu pre už pripojených účastníkov." },
    zero_recovery: { title: "Obnova z 0 %", description: "Po siedmich dňoch sa rating zvýši na 10 % a vráti sa jedno miesto. Nevyužitý Premium reset ho môže obnoviť na 100 % hneď po poklese na 0 %." },
    left_under_24h: { title: "🕒 Odchod 6–24 hodín vopred", description: "Odchod 6–24 hodín pred začiatkom bez potvrdenej účasti." },
    left_under_6h: { title: "⏰ Odchod menej ako 6 hodín vopred", description: "Odchod bez potvrdenia tesne pred začiatkom." },
    confirmed_leave: { title: "↩️ Odchod po potvrdení viac ako 24 hodín vopred", description: "Potvrdil si účasť a potom si včas odišiel." },
    confirmed_leave_under_24h: { title: "↩️ Odchod 12–24 hodín po potvrdení", description: "Potvrdil si účasť a odišiel 12–24 hodín pred začiatkom." },
    confirmed_leave_under_12h: { title: "↩️ Odchod 6–12 hodín po potvrdení", description: "Potvrdil si účasť a odišiel 6–12 hodín pred začiatkom." },
    confirmed_leave_under_6h: { title: "↩️ Odchod menej ako 6 hodín po potvrdení", description: "Potvrdil si účasť a odišiel tesne pred začiatkom." },
  },
};

const RULES_SCREEN_V3_COPY = {
  en: {
    introTitle: "How your rating changes",
    introDescription: "Your rating grows when you attend and confirm your presence. It can drop when you miss an event, leave late, or do not answer a confirmation request.",
    positiveTitle: "Your rating goes up",
    negativeTitle: "Your rating goes down",
    cancellationTitle: "If you leave an event",
    cancellationDescription: "The change depends on the time left before the event and whether you already confirmed that you are coming.",
    beforeConfirmation: "Before confirming that you are coming",
    afterConfirmation: "After confirming that you are coming",
    moreThan24Hours: "More than 24 hours before",
    sixTo24Hours: "6–24 hours before",
    twelveTo24Hours: "12–24 hours before",
    sixTo12Hours: "6–12 hours before",
    lessThan6Hours: "Less than 6 hours before",
    exceptionsTitle: "When your rating does not change",
    recoveryTitle: "If your rating reaches 0%",
    limitTitle: "Simultaneous join limit",
    limitDescription: "Without Premium, your rating sets your limit for simultaneous joins. Premium has no limit.",
    attended_event: { title: "You attend and confirm your presence", description: "Your attendance at the event is recorded." },
    fast_confirmation: { title: "You answer in time and attend", description: "Answer the request to confirm you still plan to come. You receive this bonus after attending, once per event." },
    missed_event: { title: "You miss the event or your attendance is not confirmed", description: "This applies to an event you joined as a participant." },
    request_timeout: { title: "You do not answer after a reminder", description: "The app asks if you still plan to come. Your rating drops if you do not answer even after a reminder." },
    join_grace: { title: "You joined by mistake", description: "Before confirming that you are coming, you can leave within 15 minutes if the event starts within 24 hours. Otherwise you have 6 hours." },
    host_change_waiver: { title: "The host changes the time or place", description: "A recent change can waive the rating decrease if you leave the event." },
    zero_recovery: { title: "Your rating reaches 0%", description: "After 7 days it rises to 10%, so you can join one event again. With Premium, you can restore it to 100% immediately, once per month." },
  },
  ru: {
    introTitle: "Как меняется ваш рейтинг",
    introDescription: "Рейтинг растёт за посещение и подтверждение присутствия. Он может снизиться, если вы не придёте, поздно откажетесь от участия или не ответите на запрос.",
    positiveTitle: "Рейтинг растёт",
    negativeTitle: "Рейтинг снижается",
    cancellationTitle: "Если вы отменяете участие",
    cancellationDescription: "Изменение зависит от времени до начала и от того, подтвердили ли вы, что придёте.",
    beforeConfirmation: "До подтверждения, что придёте",
    afterConfirmation: "После подтверждения, что придёте",
    moreThan24Hours: "Более чем за 24 часа",
    sixTo24Hours: "За 6–24 часа",
    twelveTo24Hours: "За 12–24 часа",
    sixTo12Hours: "За 6–12 часов",
    lessThan6Hours: "Менее чем за 6 часов",
    exceptionsTitle: "Когда рейтинг не меняется",
    recoveryTitle: "Если рейтинг упал до 0%",
    limitTitle: "Лимит одновременных записей",
    limitDescription: "Без Premium рейтинг определяет лимит одновременных записей. С Premium лимита нет.",
    attended_event: { title: "Вы пришли и подтвердили присутствие", description: "Ваше посещение мероприятия зафиксировано." },
    fast_confirmation: { title: "Вы вовремя ответили и пришли", description: "Ответьте на запрос о планах прийти. Бонус начисляется после посещения, один раз за мероприятие." },
    missed_event: { title: "Вы не пришли или присутствие не подтвердилось", description: "Это относится к мероприятию, на которое вы записались." },
    request_timeout: { title: "Вы не ответили даже после напоминания", description: "Приложение спрашивает, планируете ли вы прийти. Если не ответить, рейтинг снизится." },
    join_grace: { title: "Вы записались по ошибке", description: "Пока вы не подтвердили планы прийти, можно выйти за 15 минут, если мероприятие начинается в течение 24 часов. В остальных случаях — за 6 часов." },
    host_change_waiver: { title: "Организатор изменил время или место", description: "После недавнего изменения снижение рейтинга при выходе могут отменить." },
    zero_recovery: { title: "Рейтинг упал до 0%", description: "Через 7 дней он станет 10%, и вы снова сможете записаться на одно мероприятие. С Premium его можно сразу восстановить до 100% раз в месяц." },
  },
  sk: {
    introTitle: "Ako sa mení tvoj rating",
    introDescription: "Rating rastie, keď prídeš a potvrdíš účasť. Môže klesnúť, keď neprídeš, odhlásiš sa neskoro alebo neodpovieš na výzvu.",
    positiveTitle: "Rating rastie",
    negativeTitle: "Rating klesá",
    cancellationTitle: "Keď sa odhlásiš",
    cancellationDescription: "Záleží na tom, koľko času ostáva do začiatku a či si už potvrdil, že prídeš.",
    beforeConfirmation: "Ešte si nepotvrdil, že prídeš",
    afterConfirmation: "Už si potvrdil, že prídeš",
    moreThan24Hours: "Viac ako 24 h pred začiatkom",
    sixTo24Hours: "6–24 h pred začiatkom",
    twelveTo24Hours: "12–24 h pred začiatkom",
    sixTo12Hours: "6–12 h pred začiatkom",
    lessThan6Hours: "Menej ako 6 h pred začiatkom",
    exceptionsTitle: "Kedy sa rating nemení",
    recoveryTitle: "Keď rating klesne na 0 %",
    limitTitle: "Limit súčasných prihlásení",
    limitDescription: "Bez Premium závisí počet súčasných prihlásení od ratingu. S Premium nemáš limit.",
    attended_event: { title: "Prídeš a potvrdíš účasť", description: "Tvoja účasť na udalosti sa zaznamená." },
    fast_confirmation: { title: "Včas odpovieš a potom prídeš", description: "Odpovieš na výzvu, či stále plánuješ prísť. Bonus dostaneš až po účasti, raz za udalosť." },
    missed_event: { title: "Neprídeš alebo sa účasť nepotvrdí", description: "Platí pre udalosť, na ktorú si sa prihlásil ako účastník." },
    request_timeout: { title: "Neodpovieš ani po pripomenutí", description: "Aplikácia sa ťa opýta, či stále prídeš. Ak neodpovieš, rating klesne." },
    join_grace: { title: "Prihlásil si sa omylom", description: "Kým nepotvrdíš, že prídeš, môžeš sa bez zmeny ratingu odhlásiť do 15 minút od prihlásenia, ak sa udalosť začína do 24 hodín. Inak máš 6 hodín od prihlásenia." },
    host_change_waiver: { title: "Hostiteľ zmenil čas alebo miesto", description: "Po nedávnej zmene sa zníženie ratingu pri odhlásení môže odpustiť." },
    zero_recovery: { title: "Rating klesol na 0 %", description: "Po 7 dňoch stúpne na 10 % a znova sa môžeš prihlásiť na jednu udalosť. S Premium ho môžeš obnoviť na 100 % hneď, raz za mesiac." },
  },
  cs: {
    introTitle: "Jak se mění tvůj rating",
    introDescription: "Rating roste, když přijdeš a potvrdíš účast. Může klesnout, když nepřijdeš, odhlásíš se pozdě nebo neodpovíš na výzvu.",
    positiveTitle: "Rating roste",
    negativeTitle: "Rating klesá",
    cancellationTitle: "Když se odhlásíš",
    cancellationDescription: "Záleží na tom, kolik času zbývá do začátku a zda jsi už potvrdil, že přijdeš.",
    beforeConfirmation: "Ještě jsi nepotvrdil, že přijdeš",
    afterConfirmation: "Už jsi potvrdil, že přijdeš",
    moreThan24Hours: "Více než 24 h před začátkem",
    sixTo24Hours: "6–24 h před začátkem",
    twelveTo24Hours: "12–24 h před začátkem",
    sixTo12Hours: "6–12 h před začátkem",
    lessThan6Hours: "Méně než 6 h před začátkem",
    exceptionsTitle: "Kdy se rating nemění",
    recoveryTitle: "Když rating klesne na 0 %",
    limitTitle: "Limit současných přihlášení",
    limitDescription: "Bez Premium závisí počet současných přihlášení na ratingu. S Premium nemáš limit.",
    attended_event: { title: "Přijdeš a potvrdíš účast", description: "Tvoje účast na události se zaznamená." },
    fast_confirmation: { title: "Včas odpovíš a potom přijdeš", description: "Odpovíš na výzvu, zda stále plánuješ přijít. Bonus dostaneš až po účasti, jednou za událost." },
    missed_event: { title: "Nepřijdeš nebo se účast nepotvrdí", description: "Platí pro událost, na kterou ses přihlásil jako účastník." },
    request_timeout: { title: "Neodpovíš ani po připomenutí", description: "Aplikace se tě zeptá, zda stále přijdeš. Pokud neodpovíš, rating klesne." },
    join_grace: { title: "Přihlásil ses omylem", description: "Dokud nepotvrdíš, že přijdeš, můžeš se odhlásit do 15 minut, pokud událost začíná do 24 hodin. Jinak máš 6 hodin." },
    host_change_waiver: { title: "Pořadatel změnil čas nebo místo", description: "Po nedávné změně se snížení ratingu při odhlášení může odpustit." },
    zero_recovery: { title: "Rating klesl na 0 %", description: "Po 7 dnech stoupne na 10 % a znovu se můžeš přihlásit na jednu událost. S Premium ho můžeš obnovit na 100 % hned, jednou za měsíc." },
  },
};

function polarToCartesian(cx, cy, radius, angleDeg) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180.0;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

function describeArc(cx, cy, radius, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

function formatDate(iso, locale) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch (_) {
    return iso;
  }
}

function formatDateTime(iso, locale) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch (_) {
    return iso;
  }
}

function formatMoneyValue(value, currency = "EUR", locale = "en") {
  if (currency === "%") {
    const percent = Number(value);
    return Number.isFinite(percent) ? `${percent}%` : "0%";
  }
  const amount = Number(value);
  if (!Number.isFinite(amount)) return `0.00 ${currency || "EUR"}`;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (_) {
    return `${amount.toFixed(2)} ${currency || "EUR"}`;
  }
}

function groupTimelineByDate(items, locale) {
  const grouped = [];
  let currentKey = null;
  for (const item of items) {
    const key = formatDate(item.action_at, locale) || "Unknown";
    if (key !== currentKey) {
      grouped.push({ dateLabel: key, items: [item] });
      currentKey = key;
    } else {
      grouped[grouped.length - 1].items.push(item);
    }
  }
  return grouped;
}

function groupTicketsByDate(items, locale) {
  const grouped = [];
  let currentKey = null;
  for (const item of items) {
    const key = formatDate(item.last_activity_at, locale) || "Unknown";
    if (key !== currentKey) {
      grouped.push({ dateLabel: key, items: [item] });
      currentKey = key;
    } else {
      grouped[grouped.length - 1].items.push(item);
    }
  }
  return grouped;
}

function resolveCardAccent(item) {
  if (item?.tone === "positive") {
    return {
      border: "!border-emerald-500/70",
      capsule: "border-emerald-500/80 text-emerald-400",
    };
  }
  if (
    item?.event_key === "intent_timeout"
    || item?.event_key === "activation_timeout"
    || item?.event_key === "slot_timeout"
  ) {
    return {
      border: "!border-yellow-500/65",
      capsule: "border-yellow-500/75 text-yellow-400",
    };
  }
  if (item?.tone === "negative") {
    return {
      border: "!border-rose-500/70",
      capsule: "border-rose-500/75 text-rose-400",
    };
  }
  return {
    border: "!border-white/45",
    capsule: "border-white/35 text-white/75",
  };
}

function ParticipantRatingCloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <path
        d="M13.75 5.5L8.25 11L13.75 16.5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ParticipantRatingCloseButton({
  onClick,
  label = "Close",
  className = "",
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition hover:bg-white/14 active:scale-[0.97] ${className}`}
    >
      <ParticipantRatingCloseIcon />
    </button>
  );
}

function scrollParticipantRatingViewToTop(node) {
  if (typeof window === "undefined") return;

  const targets = new Set();
  if (node) targets.add(node);

  let parent = node?.parentElement || null;
  while (parent && parent !== document.body && parent !== document.documentElement) {
    const style = window.getComputedStyle(parent);
    const canScroll =
      /(auto|scroll|overlay)/.test(style.overflowY || "") &&
      parent.scrollHeight > parent.clientHeight;

    if (canScroll) {
      targets.add(parent);
      break;
    }
    parent = parent.parentElement;
  }

  const workspace = document.getElementById("app-workspace");
  if (workspace) targets.add(workspace);

  targets.forEach((target) => {
    if (!target) return;
    if (typeof target.scrollTo === "function") {
      target.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } else {
      target.scrollTop = 0;
      target.scrollLeft = 0;
    }
  });
}

function ZeroRatingAccordionRow({ id, title, isOpen, onToggle, children }) {
  const panelId = `${id}-panel`;

  return (
    <div className="border-b border-white/10 last:border-b-0">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => onToggle(isOpen ? null : id)}
        className="flex w-full items-center justify-between gap-4 py-4 text-left text-white transition active:scale-[0.995]"
      >
        <span className="min-w-0 text-[15px] font-semibold leading-5">{title}</span>
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/6 text-white/62 transition ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M4.5 6.75L9 11.25L13.5 6.75"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {isOpen ? (
        <div id={panelId} className="pb-4 pr-10 text-sm leading-6 text-white/72">
          {children}
        </div>
      ) : null}
    </div>
  );
}

function ZeroRatingPanel({
  copy,
  scorePercent,
  cooldownActive,
  zeroRecoveryAt,
  supportRequest,
  supportRequestSubmitting,
  onSupportRequest,
  premiumActive,
  premiumResetAvailable,
  premiumResetLastUsedAt,
  resetSubmitting,
  onPremiumReset,
  onPremiumPage,
  locale,
}) {
  const [openRow, setOpenRow] = useState(
    supportRequest ? "zero-rating-reason" : "zero-rating-mistake"
  );

  if (scorePercent > 0) {
    return (
      <div className="mt-3 rounded-[28px] border border-emerald-400/20 bg-[#13233b] p-5 text-center">
        <div className="text-xl font-semibold text-white">{copy.recoveredTitle}</div>
        <p className="mt-2 text-sm leading-6 text-white/70">{cooldownActive ? copy.cooldownInProgressDescription : copy.recoveredDescription}</p>
        {cooldownActive && zeroRecoveryAt ? (
          <p className="mt-2 text-sm text-white/70">{copy.cooldownUntil} {formatDateTime(zeroRecoveryAt, locale)}.</p>
        ) : null}
        {premiumResetAvailable ? (
          <button type="button" onClick={onPremiumReset} disabled={resetSubmitting}
            className="mt-4 inline-flex h-12 min-w-[190px] items-center justify-center rounded-full bg-emerald-500 px-6 font-semibold text-white disabled:opacity-60">
            {resetSubmitting ? copy.resetLoading : copy.resetCta}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-[28px] border border-rose-400/24 bg-[linear-gradient(180deg,rgba(244,63,94,0.13)_0%,rgba(14,35,63,0.72)_100%)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
      <div className="text-[20px] font-semibold leading-6 text-white">
        {copy.zeroStateTitle}
      </div>
      <div className="mt-3 text-sm leading-6 text-rose-50/82">
        {copy.zeroStateDescription}
      </div>
      {zeroRecoveryAt ? (
        <div className="mt-2 text-sm font-medium text-rose-50/85">
          {copy.cooldownUntil} {formatDateTime(zeroRecoveryAt, locale)}.
        </div>
      ) : null}

      <div className="mt-5 rounded-[22px] border border-white/10 bg-black/10 px-4">
        <ZeroRatingAccordionRow
          id="zero-rating-mistake"
          title={supportRequest ? copy.zeroStateRequestSentTitle : copy.zeroStateRequestTitle}
          isOpen={openRow === "zero-rating-mistake"}
          onToggle={setOpenRow}
        >
          <p>
            {supportRequest ? copy.zeroStateRequestSentDescription : copy.zeroStateRequestDescription}
          </p>
          {!supportRequest ? (
            <button
              type="button"
              onClick={onSupportRequest}
              disabled={supportRequestSubmitting}
              className="mt-4 inline-flex h-10 min-w-[154px] items-center justify-center rounded-full border border-white/14 bg-white/8 px-5 text-sm font-semibold text-white transition active:scale-[0.985] disabled:opacity-60 disabled:active:scale-100"
            >
              {supportRequestSubmitting ? copy.zeroStateRequestLoading : copy.zeroStateRequestCta}
            </button>
          ) : null}
        </ZeroRatingAccordionRow>

        <ZeroRatingAccordionRow
          id="zero-rating-reason"
          title={copy.zeroStateReasonTitle}
          isOpen={openRow === "zero-rating-reason"}
          onToggle={setOpenRow}
        >
          <p>{copy.zeroStateReasonDescription}</p>
        </ZeroRatingAccordionRow>

        <ZeroRatingAccordionRow
          id="zero-rating-premium"
          title={copy.resetIntroTitle}
          isOpen={openRow === "zero-rating-premium"}
          onToggle={setOpenRow}
        >
          <p>{copy.resetIntroDescription}</p>
        </ZeroRatingAccordionRow>

        <ZeroRatingAccordionRow
          id="zero-rating-reset-policy"
          title={copy.resetManualTitle}
          isOpen={openRow === "zero-rating-reset-policy"}
          onToggle={setOpenRow}
        >
          <p>{copy.resetManualDescription}</p>
        </ZeroRatingAccordionRow>
      </div>

      <div className="mt-5 text-center">
        {premiumActive && premiumResetAvailable ? (
          <>
            <div className="text-base font-semibold text-emerald-100">
              {copy.resetAvailableTitle}
            </div>
            <p className="mx-auto mt-2 max-w-[360px] text-sm leading-6 text-emerald-100/78">
              {copy.resetAvailableDescription}
            </p>
            <button
              type="button"
              onClick={onPremiumReset}
              disabled={resetSubmitting}
              className="mt-4 inline-flex h-12 min-w-[190px] items-center justify-center rounded-full bg-emerald-500 px-6 text-base font-semibold text-white shadow-[0_16px_32px_rgba(16,185,129,0.22)] transition active:scale-[0.985] disabled:opacity-65 disabled:active:scale-100"
            >
              {resetSubmitting ? copy.resetLoading : copy.resetCta}
            </button>
          </>
        ) : premiumActive && zeroRecoveryAt && new Date(zeroRecoveryAt).getTime() > Date.now() ? (
          <p className="mx-auto max-w-[360px] text-sm leading-6 text-white/70">
            {copy.resetReady}
          </p>
        ) : premiumActive ? (
          <>
            <div className="text-base font-semibold text-white">
              {copy.resetUsedTitle}
            </div>
            <p className="mx-auto mt-2 max-w-[360px] text-sm leading-6 text-white/68">
              {copy.resetUsedDescription}
            </p>
            {premiumResetLastUsedAt ? (
              <div className="mt-3 text-sm text-white/48">
                {copy.resetLastUsed}: {formatDate(premiumResetLastUsedAt, locale)}
              </div>
            ) : null}
          </>
        ) : (
          <button
            type="button"
            onClick={onPremiumPage}
            className="inline-flex h-12 min-w-[190px] items-center justify-center rounded-full bg-blue-500 px-6 text-base font-semibold text-white shadow-[0_16px_32px_rgba(37,99,235,0.24)] transition active:scale-[0.985]"
          >
            {copy.resetEntry}
          </button>
        )}
      </div>
    </div>
  );
}

function RatingSkeletonBlock({ className = "" }) {
  return (
    <span
      className={`vg-chat-loading-shimmer block rounded-full bg-white/[0.16] ${className}`}
      aria-hidden="true"
    />
  );
}

function ParticipantRatingSkeleton() {
  return (
    <div className="mx-auto w-full max-w-[760px] space-y-5 pt-0" aria-hidden="true">
      <div className="mx-auto flex h-[220px] w-full max-w-[430px] items-end justify-center overflow-hidden">
        <div className="relative h-[198px] w-[330px] rounded-t-full border-[24px] border-[#9db7ff]/[0.20] border-b-0 shadow-[0_0_28px_rgba(116,147,224,0.08)]">
          <RatingSkeletonBlock className="absolute left-1/2 top-[104px] h-[56px] w-[150px] -translate-x-1/2 bg-white/[0.18]" />
          <RatingSkeletonBlock className="absolute left-[54px] top-[70px] h-[22px] w-[28px] bg-white/[0.12]" />
          <RatingSkeletonBlock className="absolute right-[54px] top-[70px] h-[22px] w-[28px] bg-white/[0.12]" />
        </div>
      </div>

      <div className="rounded-[28px] border border-white/10 bg-white/[0.05] p-4">
        <RatingSkeletonBlock className="mx-auto h-[24px] w-[76%] bg-white/[0.20]" />
        <RatingSkeletonBlock className="mx-auto mt-4 h-[18px] w-[68%] bg-white/[0.12]" />
        <RatingSkeletonBlock className="mx-auto mt-3 h-[18px] w-[54%] bg-white/[0.12]" />
        <div className="mt-5 grid grid-cols-3 gap-3">
          <RatingSkeletonBlock className="h-[86px] rounded-2xl bg-white/[0.10]" />
          <RatingSkeletonBlock className="h-[86px] rounded-2xl bg-emerald-300/[0.12]" />
          <RatingSkeletonBlock className="h-[86px] rounded-2xl bg-sky-300/[0.12]" />
        </div>
      </div>

      <div>
        <RatingSkeletonBlock className="h-[26px] w-[164px] bg-white/[0.20]" />
        <div className="mt-4 space-y-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={`participant-rating-skeleton-${index}`}
              className="vg-chat-loading-row relative min-h-[74px] rounded-[16px] border border-white/20 bg-black/10 p-4"
              style={{ animationDelay: `${index * 80}ms` }}
            >
              <RatingSkeletonBlock className="h-[22px] w-[58%] bg-white/[0.16]" />
              <div className="mt-4 flex items-center gap-3">
                <RatingSkeletonBlock className="h-[18px] w-[70px] bg-white/[0.12]" />
                <RatingSkeletonBlock className="h-[18px] w-[70px] bg-white/[0.12]" />
                <RatingSkeletonBlock className="ml-auto h-[24px] w-[72px] bg-white/[0.16]" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ParticipantTicketsSkeleton() {
  return (
    <div className="mx-auto w-full max-w-[760px] space-y-8 pt-0" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={`participant-ticket-skeleton-group-${index}`} className="space-y-4">
          {index === 0 || index === 2 || index === 4 ? (
            <RatingSkeletonBlock className="h-[27px] w-[142px] bg-white/[0.20]" />
          ) : null}
          <div
            className="vg-chat-loading-row relative rounded-[22px] border border-sky-300/[0.32] bg-[#10213a]/[0.58] px-4 pb-6 pt-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
            style={{ animationDelay: `${index * 80}ms` }}
          >
            <RatingSkeletonBlock className="absolute right-5 top-[-18px] h-[36px] w-[78px] rounded-full bg-sky-300/[0.18]" />
            <div className="flex items-start gap-4">
              <RatingSkeletonBlock className="h-[64px] w-[64px] shrink-0 rounded-full bg-white/[0.14]" />
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-3 pr-[74px]">
                  <RatingSkeletonBlock className="h-[28px] w-[58%] bg-white/[0.20]" />
                  <RatingSkeletonBlock className="ml-auto mt-2 h-[20px] w-[96px] bg-white/[0.13]" />
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <RatingSkeletonBlock className="h-[28px] w-[28px] bg-white/[0.14]" />
                  <RatingSkeletonBlock className="h-[28px] w-[28px] bg-white/[0.14]" />
                  <RatingSkeletonBlock className="h-[28px] w-[28px] bg-white/[0.14]" />
                  <RatingSkeletonBlock className="h-[28px] w-[28px] bg-white/[0.14]" />
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-[20px] border border-white/10 bg-white/[0.04] px-4 py-4">
              {Array.from({ length: 3 }).map((__, lineIndex) => (
                <div
                  key={`participant-ticket-payment-skeleton-${index}-${lineIndex}`}
                  className={lineIndex > 0 ? "mt-4 flex items-center justify-between gap-5" : "flex items-center justify-between gap-5"}
                >
                  <RatingSkeletonBlock className="h-[20px] w-[42%] bg-white/[0.12]" />
                  <RatingSkeletonBlock className="h-[22px] w-[74px] bg-white/[0.18]" />
                </div>
              ))}
            </div>

            <RatingSkeletonBlock className="absolute bottom-[-15px] left-1/2 h-[30px] w-[58px] -translate-x-1/2 rounded-full bg-sky-300/[0.14]" />
          </div>
        </div>
      ))}
    </div>
  );
}

function RatingGauge({ score }) {
  const normalized = Math.max(0, Math.min(100, Number(score) || 0));
  const visiblePercent = normalized <= 0 ? 0 : normalized;
  const startAngle = -105;
  const endAngle = 105;
  const span = endAngle - startAngle;
  const activeEnd = startAngle + (span * visiblePercent) / 100;
  const radius = 102;
  const cx = 120;
  const cy = 132;
  const gradient = getParticipantRatingGradient(normalized);
  const trackPath = describeArc(cx, cy, radius, startAngle, endAngle);
  const activePath = describeArc(cx, cy, radius, startAngle, activeEnd);
  const tickRatios = [0, 0.2, 0.4, 0.6, 0.8, 1];
  const tickAngles = tickRatios.map((ratio) => startAngle + span * ratio);
  const slotLabels = [
    { value: "0", ratio: 0.03, depth: 20, fontSize: 13, dx: 6, dy: 24 },
    { value: "1", ratio: 0.1, depth: 22, fontSize: 13, dx: 0, dy: 0 },
    { value: "2", ratio: 0.3, depth: 22, fontSize: 13, dx: 0, dy: 0 },
    { value: "3", ratio: 0.5, depth: 22, fontSize: 14, dx: 0, dy: 0 },
    { value: "4", ratio: 0.7, depth: 22, fontSize: 13, dx: 0, dy: 0 },
    { value: "5", ratio: 0.9, depth: 20, fontSize: 13, dx: 0, dy: 0 },
    { value: "6", ratio: 0.97, depth: 20, fontSize: 13, dx: -6, dy: 24 },
  ].map((item) => ({
    ...item,
    angle: startAngle + span * item.ratio,
  }));

  return (
    <div className="relative mx-auto w-full max-w-[460px] overflow-visible">
      <svg viewBox="0 0 240 176" className="-mt-1 h-auto w-full overflow-visible">
        <defs>
          <linearGradient id="participant-rating-gauge-gradient" x1="0%" y1="20%" x2="100%" y2="20%">
            <stop offset="0%" stopColor={gradient.start} />
            <stop offset="100%" stopColor={gradient.end} />
          </linearGradient>
          <filter id="participant-rating-gauge-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d={trackPath}
          fill="none"
          stroke={normalized <= 0 ? "url(#participant-rating-gauge-gradient)" : "rgba(255,255,255,0.12)"}
          strokeWidth="20"
          strokeLinecap="round"
          opacity={normalized <= 0 ? "0.18" : "1"}
        />
        <path
          d={activePath}
          fill="none"
          stroke="url(#participant-rating-gauge-gradient)"
          strokeWidth="20"
          strokeLinecap="round"
          opacity={normalized <= 0 ? "0" : "0.42"}
          filter="url(#participant-rating-gauge-glow)"
        />
        <path
          d={activePath}
          fill="none"
          stroke="url(#participant-rating-gauge-gradient)"
          strokeWidth="20"
          strokeLinecap="round"
          opacity={normalized <= 0 ? "0" : "1"}
        />
        {tickAngles.map((angle) => {
          const outer = polarToCartesian(cx, cy, radius - 12, angle);
          const inner = polarToCartesian(cx, cy, radius - 21, angle);
          return (
            <line
              key={angle}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke="rgba(255,255,255,0.24)"
              strokeWidth="2"
              strokeLinecap="round"
            />
          );
        })}
        {slotLabels.map(({ value, angle, depth, fontSize, dx = 0, dy = 0 }) => {
          const point = polarToCartesian(cx, cy, radius - depth, angle);
          return (
            <text
              key={value}
              x={point.x + dx}
              y={point.y + dy}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="rgba(255,255,255,0.72)"
              fontSize={fontSize}
              fontWeight="700"
            >
              {value}
            </text>
          );
        })}
        <text
          x={cx}
          y={138}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#ffffff"
          fontSize="31"
          fontWeight="700"
        >
          {normalized}%
        </text>
      </svg>
    </div>
  );
}

function SlotsSummary({ copy, total, used, left, unlimited }) {
  return (
    <section className="mt-3 rounded-[28px] border border-white/10 bg-[#0f1d34] p-4">
      <h2 className="text-center text-lg font-semibold text-white sm:text-xl">{copy.slotsTitle}</h2>
      {!unlimited && <div className="mt-2 text-center text-sm leading-5 text-white/65">{copy.slotsDescription}</div>}
      <div className={`mt-3 grid gap-3 ${unlimited ? "grid-cols-2" : "grid-cols-3"}`}>
        <div className="rounded-2xl border border-white/10 bg-white/5 px-3 py-3 text-center">
          <div className="text-[11px] uppercase tracking-[0.12em] text-white/55">{copy.slotsUsed}</div>
          <div className="mt-2 text-2xl font-semibold text-white">{used}</div>
        </div>
        {!unlimited && (
          <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-3 py-3 text-center">
            <div className="text-[11px] uppercase tracking-[0.12em] text-emerald-100/75">{copy.slotsLeft}</div>
            <div className="mt-2 text-2xl font-semibold text-emerald-300">{left}</div>
          </div>
        )}
        <div className="rounded-2xl border border-sky-400/20 bg-sky-500/10 px-3 py-3 text-center">
          <div className="text-[11px] uppercase tracking-[0.12em] text-sky-100/75">{copy.slotsTotal}</div>
          <div className={`mt-2 font-semibold text-sky-300 ${unlimited ? "text-base sm:text-lg" : "text-2xl"}`}>
            {unlimited ? copy.slotsNoLimit : total}
          </div>
        </div>
      </div>
    </section>
  );
}

function resolveActiveSlotBadge(copy, item) {
  const key = String(item?.status_key || "joined");
  const tone = String(item?.status_tone || "neutral");

  const statusLabelMap = {
    joined: copy.activeStatusJoined,
    joined_from_waitlist: copy.activeStatusJoined,
    intent_reply_required: copy.activeStatusConfirmPlan,
    date_reply_required: copy.activeStatusConfirmPlan,
    slot_reply_required: copy.activeStatusConfirmPlan,
    intention_confirmed: copy.activeStatusPlanConfirmed,
    date_confirmed: copy.activeStatusPlanConfirmed,
    slot_confirmed: copy.activeStatusPlanConfirmed,
    attendance_reply_required: copy.activeStatusConfirmPresence,
  };

  const classNameMap = {
    positive: "border-emerald-500/70 text-emerald-300",
    warning: "border-amber-400/70 text-amber-300",
    neutral: "border-white/25 text-white/75",
  };

  return {
    label: statusLabelMap[key] || copy.activeStatusJoined,
    className: classNameMap[tone] || classNameMap.neutral,
  };
}

function ActiveFlowLegend({ copy, onOpenInfo }) {
  const steps = [
    { id: 1, label: copy.activeFlowStep1 },
    { id: 2, label: copy.activeFlowStep2 },
    { id: 3, label: copy.activeFlowStep3 },
    { id: 4, label: copy.activeFlowStep4 },
  ];

  return (
    <div className="mx-auto max-w-[560px] rounded-[24px] border border-white/10 bg-white/5 px-4 py-3">
      <div className="relative">
        <div className="text-center text-sm font-semibold text-white/90">{copy.activeFlowTitle}</div>
        <button
          type="button"
          onClick={onOpenInfo}
          aria-label={copy.activeFlowInfoAlt}
          className="absolute right-0 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-white/12 bg-white/5 text-[13px] font-bold text-white/80"
        >
          ?
        </button>
      </div>
      <div className="relative mt-4 px-2">
        <div className="absolute left-[calc(12.5%+12px)] right-[calc(12.5%+12px)] top-[11px] h-[2px] rounded-full bg-sky-200/25" />
        <div className="grid grid-cols-4 gap-1 sm:gap-2">
          {steps.map((step) => (
            <div key={step.id} className="relative flex flex-col items-center text-center">
              <div className="relative z-10 flex h-6 w-6 items-center justify-center rounded-full border border-sky-400/35 bg-[#13233b] text-[11px] font-bold text-sky-300">
                {step.id}
              </div>
              <div className="mt-2 text-[11px] font-medium leading-4 text-white/65 sm:text-xs">
                {step.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActiveFlowInfoModal({ copy, open, onClose }) {
  const stepDetails = [
    { id: 1, title: copy.activeFlowInfoStep1Title, description: copy.activeFlowInfoStep1Description },
    { id: 2, title: copy.activeFlowInfoStep2Title, description: copy.activeFlowInfoStep2Description },
    { id: 3, title: copy.activeFlowInfoStep3Title, description: copy.activeFlowInfoStep3Description },
    { id: 4, title: copy.activeFlowInfoStep4Title, description: copy.activeFlowInfoStep4Description },
  ];

  return (
    <AppModalShell
      open={open}
      title={copy.activeFlowInfoTitle}
      onClose={onClose}
      closeAriaLabel={copy.closeAlt}
      maxWidthClassName="max-w-[560px]"
      desktopMaxHeight="min(82vh, 640px)"
    >
      <div className="space-y-3">
        {stepDetails.map((step) => (
          <div key={step.id} className="rounded-3xl border border-white/10 bg-white/5 p-4">
            <div className="text-base font-semibold text-white">{step.title}</div>
            <div className="mt-2 text-sm leading-6 text-white/75">{step.description}</div>
          </div>
        ))}
      </div>
    </AppModalShell>
  );
}

function ActiveSlotsSection({ copy, items, onOpen, onOpenInfo }) {
  const [expanded, setExpanded] = useState(false);
  const safeItems = Array.isArray(items) ? items : [];
  if (safeItems.length === 0) return null;

  const visibleItems = expanded ? safeItems : safeItems.slice(0, 1);

  return (
    <section className="mt-3 rounded-[28px] border border-white/10 bg-[#0f1d34] p-4">
      <h2 className="text-center text-lg font-semibold text-white sm:text-xl">{copy.activeSlotsTitle}</h2>
      <div className="mt-2 text-center text-sm leading-5 text-white/65">{copy.activeSlotsDescription}</div>
      <div className="mt-5 space-y-3">
        {visibleItems.map((item) => {
          const preview = item?.event_preview || {};
          const badge = resolveActiveSlotBadge(copy, item);
          return (
            <button
              key={`${item?.event_id || "na"}:${item?.joined_at || "na"}`}
              type="button"
              onClick={() => onOpen(item)}
              className="block w-full text-left"
            >
              {item?.counts_toward_limit === false && (
                <span className="mb-4 block text-center text-xs font-medium text-sky-200/85">
                  {copy.activeJoinNotCounted}
                </span>
              )}
              <EventCard
                title={preview?.name || ""}
                avatar={null}
                isOnline={Number(preview?.event_format) === 0}
                isPersonal={Number(preview?.host_format) === 0}
                isPaid={Number(preview?.fee_type) === 1}
                isByRequest={Boolean(preview?.join_by_request)}
                minVaultLevel={preview?.min_vault_level}
                datetime={preview?.date_time}
                isVerified={Boolean(preview?.is_verified)}
                isValidated={Boolean(preview?.is_validated ?? true)}
                isFavourite={false}
                onToggleFavourite={() => {}}
                status={preview?.status || "active"}
                badgeLabel={preview?.badge_label || null}
                iconImageUrl={preview?.icon_image_url}
                type={preview?.type || "event"}
                showFavouriteButton={false}
                topRightBadge={badge.label}
                topRightBadgeClassName={badge.className}
                titleMaxWidthClass="max-w-[380px]"
                suppressStatusOverlay
              />
            </button>
          );
        })}
      </div>
      {safeItems.length > 1 ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            className="inline-flex h-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white"
          >
            {expanded ? copy.activeSlotsShowLess : copy.activeSlotsShowAll}
          </button>
        </div>
      ) : null}
      <div className="mt-5">
        <ActiveFlowLegend copy={copy} onOpenInfo={onOpenInfo} />
      </div>
    </section>
  );
}

function TimelineEventCard({
  item,
  onOpen,
  reasonTitle,
  reasonDescription,
  reasonFooter,
  reasonOpen,
  onToggleReason,
  reasonToggleLabel,
  reasonSectionLabel,
}) {
  const preview = item?.event_preview || {};
  const accent = resolveCardAccent(item);
  const delta = Number(item?.delta);
  const showDelta = Number.isFinite(delta) && delta !== 0;
  const hasReason = Boolean(reasonTitle || reasonDescription || reasonFooter);
  const handleCardClick = () => {
    if (hasReason && !reasonOpen) {
      onToggleReason?.();
      return;
    }
    onOpen(item);
  };

  return (
    <div>
      <div
        className={`relative overflow-visible rounded-2xl border bg-[#13233b] shadow-[0_10px_24px_rgba(0,0,0,0.18)] transition ${
          reasonOpen ? "bg-[#183258] shadow-[0_14px_32px_rgba(37,99,235,0.22)] ring-2 ring-sky-300/35" : ""
        } ${accent.border}`}
      >
        {topRightBadge(showDelta, delta, accent.capsule)}

        <button
          type="button"
          onClick={handleCardClick}
          className="block w-full text-left"
        >
          <EventCard
            title={preview?.name || item?.event_name || item?.title}
            avatar={null}
            isOnline={Number(preview?.event_format) === 0}
            isPersonal={Number(preview?.host_format) === 0}
            isPaid={Number(preview?.fee_type) === 1}
            isByRequest={Boolean(preview?.join_by_request)}
            minVaultLevel={preview?.min_vault_level}
            datetime={preview?.date_time || item?.event_date_time}
            isVerified={Boolean(preview?.is_verified)}
            isValidated={Boolean(preview?.is_validated ?? true)}
            isFavourite={false}
            onToggleFavourite={() => {}}
            status={preview?.status || "active"}
            badgeLabel={preview?.badge_label || null}
            iconImageUrl={preview?.icon_image_url}
            type={preview?.type || "event"}
            showFavouriteButton={false}
            topRightBadge={null}
            topRightBadgeClassName=""
            cardClassName="!border-transparent !bg-transparent shadow-none"
            titleMaxWidthClass="max-w-[420px]"
            suppressStatusOverlay
          />
        </button>

        {hasReason ? (
          <>
            <div
              className={`grid transition-all duration-200 ease-out ${
                reasonOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <div className="px-4 pb-5 pt-1">
                  <div className="border-t border-white/10 pt-4">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">
                      {reasonSectionLabel}
                    </div>
                    {reasonTitle ? (
                      <div className="mt-2 text-sm font-semibold leading-6 text-white">
                        {reasonTitle}
                      </div>
                    ) : null}
                    {reasonDescription ? (
                      <div className="mt-2 text-sm leading-6 text-white/70">
                        {reasonDescription}
                      </div>
                    ) : null}
                    {reasonFooter ? <div className="mt-4">{reasonFooter}</div> : null}
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleReason?.();
              }}
              aria-expanded={reasonOpen}
              aria-label={reasonToggleLabel}
              className={`absolute bottom-0 left-1/2 z-20 flex h-6 min-w-[36px] -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full border bg-[#13233b] px-2 shadow-[0_8px_16px_rgba(0,0,0,0.18)] transition ${accent.capsule}`}
            >
              <svg
                width="11"
                height="6"
                viewBox="0 0 11 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`transition-transform duration-200 ${reasonOpen ? "rotate-180" : ""}`}
              >
                <path
                  d="M1 1L5.5 5L10 1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function localizeTimelineItem(item, copy) {
  if (!item) return item;
  if (item?.event_key === "zero_cooldown_recovery") {
    return { ...item, title: copy.zeroRecoveryTitle || item.title, description: "", force_system_card: true };
  }
  if (item?.event_key === "premium_reset") {
    return {
      ...item,
      title: copy.premiumResetTitle,
      description: "",
    };
  }
  if (item?.event_key === "manual_attendance_review_approved") {
    return {
      ...item,
      title: copy.manualAttendanceApprovedTitle,
      description: "",
      force_system_card: true,
    };
  }
  return item;
}

function resolveTimelineReason(item, copy, lang) {
  if (!item) return { title: "", description: "" };
  const v3Log = Number(item?.policy_version) >= 3;

  const attendanceReviewStatus = String(item?.attendance_review?.status || "").trim().toLowerCase();
  if (
    (item?.event_key === "missed" || item?.event_key === "attendance_not_confirmed")
    && (attendanceReviewStatus === "approved" || item?.manual_review_approved)
  ) {
    return {
      title: copy.manualReviewAdjustedReasonTitle,
      description: v3Log
        ? copy.manualReviewAdjustedReasonDescription
        : (COPY[lang] || COPY.en).manualReviewAdjustedReasonDescription,
    };
  }

  const ruleCopy = v3Log ? RULE_COPY_V3 : RULE_COPY;
  const localizedRules = ruleCopy[lang] || ruleCopy.en;
  const copyKeyMap = {
    attended: "attended_event",
    fast_confirmation: "fast_confirmation",
    missed: "missed_event",
    attendance_not_confirmed: "missed_event",
    leave_under_6h: "left_under_6h",
    leave_under_24h: "left_under_24h",
    leave_after_grace: "leave_after_grace",
    request_timeout: "request_timeout",
    intent_timeout: "request_timeout",
    activation_timeout: "request_timeout",
    slot_timeout: "request_timeout",
    confirmed_leave: "confirmed_leave",
    confirmed_leave_under_24h: "confirmed_leave_under_24h",
    confirmed_leave_under_12h: "confirmed_leave_under_12h",
    confirmed_leave_under_6h: "confirmed_leave_under_6h",
    dinner_attended: "dinner_attended",
    dinner_fast_confirmation: "dinner_fast_confirmation",
    dinner_vote_timeout: "dinner_vote_timeout",
    dinner_left_table: "dinner_left_table",
    dinner_missed: "dinner_missed",
  };

  const copyKey = copyKeyMap[item?.event_key];
  const localized = copyKey ? localizedRules?.[copyKey] : null;

  return {
    title: localized?.title || item?.title || "",
    description: localized?.description || item?.description || "",
  };
}

function mergeApprovedAttendanceReviewTimeline(items) {
  if (!Array.isArray(items) || items.length === 0) return [];

  const manualApprovalByEventId = new Map();
  const missedByEventId = new Map();

  items.forEach((item) => {
    const eventId = Number(item?.event_id);
    if (!Number.isFinite(eventId) || eventId <= 0) return;

    if (item?.event_key === "missed" || item?.event_key === "attendance_not_confirmed") {
      missedByEventId.set(eventId, item);
      return;
    }

    if (item?.event_key === "manual_attendance_review_approved") {
      manualApprovalByEventId.set(eventId, item);
    }
  });

  const merged = items.flatMap((item) => {
    const eventId = Number(item?.event_id);
    const hasEventId = Number.isFinite(eventId) && eventId > 0;

    if (
      hasEventId &&
      item?.event_key === "manual_attendance_review_approved" &&
      missedByEventId.has(eventId)
    ) {
      return [];
    }

    if (
      hasEventId &&
      (item?.event_key === "missed" || item?.event_key === "attendance_not_confirmed")
    ) {
      const manualApprovalItem = manualApprovalByEventId.get(eventId);
      if (manualApprovalItem && manualApprovalItem.id !== item.id) {
        const extraLogIds = [
          ...new Set(
            [
              ...(Array.isArray(item?.extra_log_ids) ? item.extra_log_ids : []),
              manualApprovalItem?.log_id,
            ].filter(Boolean)
          ),
        ];

        return [{
          ...item,
          delta: (Number(item?.delta) || 0) + (Number(manualApprovalItem?.delta) || 0),
          action_at: manualApprovalItem?.action_at || item?.action_at,
          is_unread: Boolean(item?.is_unread || manualApprovalItem?.is_unread),
          extra_log_ids: extraLogIds,
          manual_review_approved: true,
          policy_version: Math.max(Number(item?.policy_version) || 2, Number(manualApprovalItem?.policy_version) || 2),
          attendance_review: {
            ...(item?.attendance_review || {}),
            status: "approved",
          },
        }];
      }
    }

    return [item];
  });

  return merged.sort((a, b) => {
    const aTs = a?.action_at ? new Date(a.action_at).getTime() : 0;
    const bTs = b?.action_at ? new Date(b.action_at).getTime() : 0;
    return bTs - aTs;
  });
}

function TimelineSystemCard({ item }) {
  const accent = resolveCardAccent(item);
  const delta = Number(item?.delta);
  const showDelta = Number.isFinite(delta) && delta !== 0;

  return (
    <div className={`relative rounded-2xl border px-4 py-5 text-center shadow-[0_10px_24px_rgba(0,0,0,0.18)] ${accent.border} bg-[#13233b]`}>
      {topRightBadge(showDelta, delta, accent.capsule)}
      <div className="text-lg font-extrabold text-white">{item?.title}</div>
      {item?.description ? (
        <div className="mt-2 text-sm font-medium leading-6 text-white/70">{item.description}</div>
      ) : null}
    </div>
  );
}

function topRightBadge(showDelta, delta, capsuleClassName) {
  if (!showDelta) return null;
  return (
    <div
      className={`absolute right-3 top-0 z-20 -translate-y-1/2 rounded-full border bg-[#13233b] px-4 py-1 text-[15px] font-extrabold ${capsuleClassName}`}
    >
      {delta > 0 ? `+${delta}%` : `${delta}%`}
    </div>
  );
}

function TimelineObservedCard({ item, locale, onOpen, onViewed, copy, onRequestReview, requestSubmittingId }) {
  const rowRef = useRef(null);
  const didNotifyRef = useRef(false);
  const previousItemIdRef = useRef(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  const attendanceReview = item?.attendance_review || null;
  const attendanceReviewStatus = String(attendanceReview?.status || "").trim().toLowerCase();
  const canRequestReview = Boolean(attendanceReview?.can_request && item?.event_id);
  const showPending = attendanceReviewStatus === "pending";
  const showRejected = attendanceReviewStatus === "rejected";
  const lang = String(locale || "en").split("-")[0];
  const reason = resolveTimelineReason(item, copy, lang);
  const reasonFooter = canRequestReview ? (
    <div className="space-y-3">
      <div className="text-sm leading-6 text-white/75">
        {copy.reviewRequestDescription}
      </div>
      <div className="text-sm leading-6 text-white/70">
        {copy.reviewRequestPenalty}
      </div>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onRequestReview(item);
        }}
        className="inline-flex h-10 items-center justify-center rounded-2xl border border-white/12 bg-white/5 px-4 text-sm font-semibold text-white"
      >
        {requestSubmittingId === item?.id ? copy.reviewRequestSending : copy.reviewRequestCta}
      </button>
    </div>
  ) : showPending ? (
    <div className="inline-flex rounded-2xl border border-sky-400/20 bg-sky-500/10 px-4 py-2 text-sm font-medium text-sky-100/85">
      {copy.reviewRequestPending}
    </div>
  ) : showRejected ? (
    <div className="inline-flex rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-2 text-sm font-medium text-rose-100/85">
      {copy.reviewRequestRejected}
    </div>
  ) : null;
  const hasExpandableReason = Boolean(
    item?.event_id
    && !item?.force_system_card
    && (reason.title || reason.description || reasonFooter)
  );

  useEffect(() => {
    didNotifyRef.current = false;
  }, [item?.id]);

  useEffect(() => {
    if (previousItemIdRef.current === item?.id) return;
    previousItemIdRef.current = item?.id;
    setReasonOpen(Boolean(item?.is_unread && hasExpandableReason));
  }, [hasExpandableReason, item?.id, item?.is_unread]);

  useEffect(() => {
    if (!item?.is_unread || !rowRef.current) return undefined;

    const node = rowRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.68 || didNotifyRef.current) {
            return;
          }
          didNotifyRef.current = true;
          onViewed(item);
        });
      },
      {
        threshold: [0.68],
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [item, onViewed]);

  return (
    <div ref={rowRef} className="pb-4">
      {item?.event_id && !item?.force_system_card ? (
        <TimelineEventCard
          item={item}
          onOpen={onOpen}
          reasonTitle={reason.title}
          reasonDescription={reason.description}
          reasonFooter={reasonFooter}
          reasonOpen={reasonOpen}
          onToggleReason={hasExpandableReason ? () => setReasonOpen((current) => !current) : null}
          reasonToggleLabel={reasonOpen ? copy.hideReasonAlt : copy.showReasonAlt}
          reasonSectionLabel={copy.changeReasonTitle}
        />
      ) : (
        <TimelineSystemCard item={item} />
      )}
    </div>
  );
}

function resolveTicketStatus(copy, status) {
  const key = String(status || "paid");
  if (key === "refunded") {
    return {
      label: copy.ticketStatusRefunded,
      className: "border-emerald-500/70 text-emerald-300",
      border: "!border-emerald-500/70",
    };
  }
  if (key === "refund_pending") {
    return {
      label: copy.ticketStatusRefundPending,
      className: "border-amber-400/70 text-amber-300",
      border: "!border-amber-400/70",
    };
  }
  if (key === "refund_rejected") {
    return {
      label: copy.ticketStatusRefundRejected,
      className: "border-rose-500/75 text-rose-300",
      border: "!border-rose-500/70",
    };
  }
  return {
    label: copy.ticketStatusPaid,
    className: "border-sky-400/65 text-sky-300",
    border: "!border-sky-400/55",
  };
}

function TicketAmountRow({ label, value, currency, locale, tone = "default" }) {
  const valueClass = tone === "positive" ? "text-emerald-300" : tone === "negative" ? "text-rose-300" : "text-white";
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-white/55">{label}</span>
      <span className={`font-semibold ${valueClass}`}>{formatMoneyValue(value, currency, locale)}</span>
    </div>
  );
}

function TicketLedgerCard({ item, copy, locale, onOpen }) {
  const [open, setOpen] = useState(false);
  const preview = item?.event_preview || {};
  const status = resolveTicketStatus(copy, item?.status);
  const payments = Array.isArray(item?.payments) ? item.payments : [];
  const cancellations = Array.isArray(item?.cancellations) ? item.cancellations : [];
  const currency = payments[0]?.currency || "EUR";
  const hasDetails = payments.length > 0 || cancellations.length > 0;

  return (
    <div className="pb-4">
      <div className={`relative overflow-visible rounded-2xl border bg-[#13233b] shadow-[0_10px_24px_rgba(0,0,0,0.18)] ${status.border}`}>
        <div className={`absolute right-3 top-0 z-20 -translate-y-1/2 rounded-full border bg-[#13233b] px-4 py-1 text-[13px] font-extrabold ${status.className}`}>
          {status.label}
        </div>

        <button
          type="button"
          onClick={() => onOpen(item)}
          className="block w-full pt-4 text-left"
        >
          <EventCard
            title={preview?.name || ""}
            avatar={null}
            isOnline={Number(preview?.event_format) === 0}
            isPersonal={Number(preview?.host_format) === 0}
            isPaid={Number(preview?.fee_type) === 1}
            isByRequest={Boolean(preview?.join_by_request)}
            minVaultLevel={preview?.min_vault_level}
            datetime={preview?.date_time}
            isVerified={Boolean(preview?.is_verified)}
            isValidated={Boolean(preview?.is_validated ?? true)}
            isFavourite={false}
            onToggleFavourite={() => {}}
            status={preview?.status || "active"}
            badgeLabel={preview?.badge_label || null}
            iconImageUrl={preview?.icon_image_url}
            type={preview?.type || "event"}
            showFavouriteButton={false}
            topRightBadge={null}
            topRightBadgeClassName=""
            cardClassName="!border-transparent !bg-transparent shadow-none"
            titleMaxWidthClass="max-w-[420px]"
            suppressStatusOverlay
          />
        </button>

        <div className="px-4 pb-5 pt-1">
          <div className="grid gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <TicketAmountRow label={copy.ticketPaidTotal} value={item?.paid_total} currency={currency} locale={locale} />
            <TicketAmountRow label={copy.ticketBaseAmount} value={item?.ticket_base_total} currency={currency} locale={locale} />
            {Number(item?.service_fee_total) > 0 ? (
              <TicketAmountRow label={copy.ticketServiceFee} value={item.service_fee_total} currency={currency} locale={locale} />
            ) : null}
            {Number(item?.refunded_total) > 0 ? (
              <TicketAmountRow label={copy.ticketRefunded} value={item?.refunded_total} currency={currency} locale={locale} tone="positive" />
            ) : null}
          </div>
        </div>

        {hasDetails ? (
          <>
            <div
              className={`grid transition-all duration-200 ease-out ${
                open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <div className="space-y-4 border-t border-white/10 px-4 pb-6 pt-4">
                  {payments.length > 0 ? (
                    <div>
                      <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">
                        {copy.ticketPaymentsTitle}
                      </div>
                      <div className="space-y-3">
                        {payments.map((payment) => (
                          <div key={`payment-${payment.id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="text-sm font-semibold text-white">{copy.ticketPaymentLabel}</div>
                                <div className="mt-1 text-xs text-white/45">
                                  {formatDateTime(payment.occurred_at || payment.paid_at || payment.created_at, locale)}
                                </div>
                              </div>
                              <div className="text-right text-sm font-semibold text-white">
                                {formatMoneyValue(payment.amount, payment.currency, locale)}
                              </div>
                            </div>
                            <div className="mt-3 grid gap-2">
                              <TicketAmountRow label={copy.ticketBaseAmount} value={payment.ticket_base_amount} currency={payment.currency} locale={locale} />
                              {Number(payment.service_fee_amount) > 0 ? (
                                <TicketAmountRow label={copy.ticketServiceFee} value={payment.service_fee_amount} currency={payment.currency} locale={locale} />
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {cancellations.length > 0 ? (
                    <div>
                      <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">
                        {copy.ticketRefundsTitle}
                      </div>
                      <div className="space-y-3">
                        {cancellations.map((request) => (
                          <div key={`refund-${request.id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="text-sm font-semibold text-white">{copy.ticketRefundLabel}</div>
                                <div className="mt-1 text-xs text-white/45">
                                  {formatDateTime(request.resolved_at || request.requested_at, locale)}
                                </div>
                              </div>
                              <div className="text-right text-sm font-semibold text-emerald-300">
                                {formatMoneyValue(request.estimated_refund_amount, currency, locale)}
                              </div>
                            </div>
                            <div className="mt-3 grid gap-2">
                              <TicketAmountRow label={copy.ticketRefundPercent} value={request.policy_refund_percent} currency="%" locale={locale} />
                              <TicketAmountRow label={copy.ticketPolicyDeduction} value={request.policy_deduction_amount} currency={currency} locale={locale} tone="negative" />
                              {Number(request.service_fee_amount) > 0 ? (
                                <TicketAmountRow label={copy.ticketServiceFee} value={request.service_fee_amount} currency={currency} locale={locale} />
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setOpen((current) => !current);
              }}
              aria-expanded={open}
              className={`absolute bottom-0 left-1/2 z-20 flex h-7 min-w-[42px] -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full border bg-[#13233b] px-3 shadow-[0_10px_20px_rgba(0,0,0,0.2)] transition ${status.className}`}
            >
              <svg
                width="12"
                height="6"
                viewBox="0 0 12 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              >
                <path
                  d="M1 1L6 5L11 1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function MyTicketsSection({ copy, items, locale, onOpen, showTitle = true }) {
  const safeItems = Array.isArray(items) ? items : [];
  const groups = groupTicketsByDate(safeItems, locale);

  return (
    <section className="mt-6">
      {showTitle ? (
        <h2 className="mb-4 text-lg font-semibold text-white">{copy.ticketsTitle}</h2>
      ) : null}
      {groups.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-[#0f1d34] p-5 text-sm text-white/60">
          {copy.ticketsEmpty}
        </div>
      ) : (
        <div className="space-y-5 pb-2">
          {groups.map((group) => (
            <div key={group.dateLabel}>
              <div className="mb-2 text-[15px] font-semibold text-white/95">{group.dateLabel}</div>
              <div className="space-y-4">
                {group.items.map((item) => (
                  <TicketLedgerCard
                    key={item.event_id}
                    item={item}
                    copy={copy}
                    locale={locale}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function formatRulesImpact(impact) {
  if (typeof impact !== "number") return "—";
  return `${impact > 0 ? "+" : ""}${impact} %`;
}

function RulesImpactCard({ rule, localizedRule, tone }) {
  const isPositive = tone === "positive";
  return (
    <div className={`rounded-2xl border p-4 ${isPositive ? "border-emerald-400/25 bg-emerald-500/10" : "border-rose-400/25 bg-rose-500/10"}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold leading-6 text-white">
          {localizedRule?.title || rule.title}
        </h3>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-sm font-bold ${isPositive ? "bg-emerald-400/10 text-emerald-200" : "bg-rose-400/10 text-rose-200"}`}>
          {formatRulesImpact(rule.impact)}
        </span>
      </div>
      {(localizedRule?.description || rule.description) && (
        <p className="mt-2 text-sm leading-6 text-white/75">
          {localizedRule?.description || rule.description}
        </p>
      )}
    </div>
  );
}

function RulesCancellationColumn({ title, rows, ruleById }) {
  return (
    <div className="rounded-2xl border border-white/15 bg-[#13233b]/80 p-4">
      <h3 className="min-h-[48px] text-base font-semibold leading-6 text-white">{title}</h3>
      <div className="mt-3 divide-y divide-white/10 border-t border-white/10">
        {rows.map(({ label, ruleId }) => {
          const impact = ruleById.get(ruleId)?.impact;
          return (
            <div key={ruleId} className="flex items-center justify-between gap-3 py-3 text-sm">
              <span className="leading-5 text-white/80">{label}</span>
              <span className={`shrink-0 font-bold ${impact < 0 ? "text-rose-200" : "text-emerald-200"}`}>
                {formatRulesImpact(impact)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RulesScreenV3({ rules, lang, embedded }) {
  const copy = RULES_SCREEN_V3_COPY[lang] || RULES_SCREEN_V3_COPY.en;
  const legacyCopy = RULE_COPY_V3[lang] || RULE_COPY_V3.en;
  const positiveRules = Array.isArray(rules?.positive_rules) ? rules.positive_rules : [];
  const neutralRules = Array.isArray(rules?.neutral_rules) ? rules.neutral_rules : [];
  const negativeRules = Array.isArray(rules?.negative_rules) ? rules.negative_rules : [];
  const ruleById = new Map([...positiveRules, ...neutralRules, ...negativeRules].map((rule) => [rule.id, rule]));
  const cancellationIds = [
    "early_leave",
    "left_under_24h",
    "left_under_6h",
    "confirmed_leave",
    "confirmed_leave_under_24h",
    "confirmed_leave_under_12h",
    "confirmed_leave_under_6h",
  ];
  const showCancellationComparison = cancellationIds.every((id) => ruleById.has(id));
  const directNegativeRules = negativeRules.filter((rule) =>
    !showCancellationComparison || !cancellationIds.includes(rule.id)
  );
  const neutralExceptions = neutralRules.filter((rule) =>
    rule.id !== "zero_recovery" && (!showCancellationComparison || rule.id !== "early_leave")
  );
  const recoveryRule = ruleById.get("zero_recovery");
  const cancellationBeforeRows = [
    { label: copy.moreThan24Hours, ruleId: "early_leave" },
    { label: copy.sixTo24Hours, ruleId: "left_under_24h" },
    { label: copy.lessThan6Hours, ruleId: "left_under_6h" },
  ];
  const cancellationAfterRows = [
    { label: copy.moreThan24Hours, ruleId: "confirmed_leave" },
    { label: copy.twelveTo24Hours, ruleId: "confirmed_leave_under_24h" },
    { label: copy.sixTo12Hours, ruleId: "confirmed_leave_under_12h" },
    { label: copy.lessThan6Hours, ruleId: "confirmed_leave_under_6h" },
  ];
  const limitRanges = [
    { rating: "0 %", joins: 0 },
    { rating: "1–20 %", joins: 1 },
    { rating: "21–40 %", joins: 2 },
    { rating: "41–60 %", joins: 3 },
    { rating: "61–80 %", joins: 4 },
    { rating: "81–99 %", joins: 5 },
    { rating: "100 %", joins: 6 },
  ];

  return (
    <div className={`mx-auto w-full max-w-[760px] space-y-7 text-white ${embedded ? "pb-4" : "pb-12"}`}>
      <div className="rounded-2xl border border-sky-300/25 bg-sky-500/10 p-5">
        <h2 className="text-xl font-semibold leading-7 text-white">{copy.introTitle}</h2>
        <p className="mt-2 text-sm leading-6 text-sky-50/85">{copy.introDescription}</p>
      </div>

      {positiveRules.length > 0 && (
        <section>
          <h2 className="mb-3 text-xl font-semibold text-white">{copy.positiveTitle}</h2>
          <div className="space-y-3">
            {positiveRules.map((rule) => (
              <RulesImpactCard key={rule.id} rule={rule} localizedRule={copy[rule.id] || legacyCopy[rule.id]} tone="positive" />
            ))}
          </div>
        </section>
      )}

      {directNegativeRules.length > 0 && (
        <section>
          <h2 className="mb-3 text-xl font-semibold text-white">{copy.negativeTitle}</h2>
          <div className="space-y-3">
            {directNegativeRules.map((rule) => (
              <RulesImpactCard key={rule.id} rule={rule} localizedRule={copy[rule.id] || legacyCopy[rule.id]} tone="negative" />
            ))}
          </div>
        </section>
      )}

      {showCancellationComparison && (
        <section>
          <h2 className="mb-2 text-xl font-semibold text-white">{copy.cancellationTitle}</h2>
          <p className="mb-3 text-sm leading-6 text-white/75">{copy.cancellationDescription}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <RulesCancellationColumn title={copy.beforeConfirmation} rows={cancellationBeforeRows} ruleById={ruleById} />
            <RulesCancellationColumn title={copy.afterConfirmation} rows={cancellationAfterRows} ruleById={ruleById} />
          </div>
        </section>
      )}

      {neutralExceptions.length > 0 && (
        <section>
          <h2 className="mb-3 text-xl font-semibold text-white">{copy.exceptionsTitle}</h2>
          <div className="space-y-3">
            {neutralExceptions.map((rule) => {
              const localizedRule = copy[rule.id] || legacyCopy[rule.id] || rule;
              return (
                <div key={rule.id} className="rounded-2xl border border-white/15 bg-white/5 p-4">
                  <h3 className="text-base font-semibold leading-6 text-white">{localizedRule.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-white/75">{localizedRule.description}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {recoveryRule && (
        <section>
          <h2 className="mb-3 text-xl font-semibold text-white">{copy.recoveryTitle}</h2>
          <div className="rounded-2xl border border-sky-300/20 bg-sky-500/10 p-4">
            <p className="text-sm leading-6 text-white/85">{copy.zero_recovery?.description || recoveryRule.description}</p>
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-xl font-semibold text-white">{copy.limitTitle}</h2>
        <p className="text-sm leading-6 text-white/75">{copy.limitDescription}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {limitRanges.map(({ rating, joins }) => (
            <div key={rating} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#13233b]/80 px-3 py-2 text-sm">
              <span className="text-white/75">{rating}</span>
              <span className="text-white/40" aria-hidden="true">→</span>
              <strong className="font-semibold text-sky-200">{joins}</strong>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function RulesScreen({ copy, rules, lang, embedded = false }) {
  if (Number(rules?.policy_version) >= 3) {
    return <RulesScreenV3 rules={rules} lang={lang} embedded={embedded} />;
  }
  const ruleCopy = Number(rules?.policy_version) >= 3 ? RULE_COPY_V3 : RULE_COPY;
  const localizedRules = ruleCopy[lang] || ruleCopy.en;
  const positiveRules = Array.isArray(rules?.positive_rules) ? rules.positive_rules : [];
  const neutralRules = Array.isArray(rules?.neutral_rules) ? rules.neutral_rules : [];
  const negativeRules = Array.isArray(rules?.negative_rules) ? rules.negative_rules : [];
  const slotRules = [
    { range: "0%", slots: "0" },
    { range: "1% - 20%", slots: "1" },
    { range: "21% - 40%", slots: "2" },
    { range: "41% - 60%", slots: "3" },
    { range: "61% - 80%", slots: "4" },
    { range: "81% - 99%", slots: "5" },
    { range: "100%", slots: "6" },
  ];

  return (
    <div className={`mx-auto w-full max-w-[760px] text-white ${embedded ? "pb-4" : "pb-12"}`}>
          <div className="mb-8 rounded-3xl border border-sky-400/20 bg-sky-500/10 p-5">
            <div className="text-base font-semibold text-white">
              {localizedRules.introTitle || copy.rulesIntroTitle}
            </div>
            <div className="mt-2 text-sm leading-6 text-sky-100/85">
              {localizedRules.introDescription || copy.rulesIntroDescription}
            </div>
          </div>

          <section className="mb-8">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.24em] text-white/45">
              {copy.slotRulesTitle}
            </h2>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
              <div className="text-sm leading-6 text-white/75">
                {copy.slotRulesDescription}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {slotRules.map((item) => (
                  <div
                    key={item.range}
                    className="rounded-2xl border border-white/10 bg-[#13233b] px-4 py-4 text-center"
                  >
                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">
                      {item.range}
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-sky-300">
                      {item.slots}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {positiveRules.length > 0 && (
            <section className="mb-8">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.24em] text-white/45">
                {copy.positiveRulesTitle}
              </h2>
              <div className="space-y-3">
                {positiveRules.map((rule) => (
                  <div key={rule.id} className="rounded-3xl border border-emerald-400/20 bg-emerald-500/10 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="text-base font-medium text-white">
                        {(localizedRules[rule.id] || {}).title || rule.title}
                      </div>
                      {typeof rule.impact === "number" && (
                        <div className={`text-sm font-semibold ${rule.impact >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                          {rule.impact > 0 ? `+${rule.impact}%` : `${rule.impact}%`}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-sm leading-6 text-white/70">
                      {(localizedRules[rule.id] || {}).description || rule.description}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {neutralRules.length > 0 && (
            <section className="mb-8">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.24em] text-white/45">
                {copy.neutralRulesTitle}
              </h2>
              <div className="space-y-3">
                {neutralRules.map((rule) => (
                  <div key={rule.id} className="rounded-3xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="text-base font-medium text-white">
                        {(localizedRules[rule.id] || {}).title || rule.title}
                      </div>
                      {typeof rule.impact === "number" && (
                        <div className="text-sm font-semibold text-white/45">
                          {rule.impact > 0 ? `+${rule.impact}%` : `${rule.impact}%`}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-sm leading-6 text-white/70">
                      {(localizedRules[rule.id] || {}).description || rule.description}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {negativeRules.length > 0 && (
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.24em] text-white/45">
                {copy.negativeRulesTitle}
              </h2>
              <div className="space-y-3">
                {negativeRules.map((rule) => (
                  <div key={rule.id} className="rounded-3xl border border-rose-400/20 bg-rose-500/10 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="text-base font-medium text-white">
                        {(localizedRules[rule.id] || {}).title || rule.title}
                      </div>
                      {typeof rule.impact === "number" && (
                        <div className={`text-sm font-semibold ${rule.impact >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                          {rule.impact > 0 ? `+${rule.impact}%` : `${rule.impact}%`}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-sm leading-6 text-white/70">
                      {(localizedRules[rule.id] || {}).description || rule.description}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
    </div>
  );
}

export default function ParticipantRatingPage({ mode = "rating", embedded = false, onClose = null }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation("events");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [screen, setScreen] = useState("main");
  const [isActiveFlowInfoOpen, setIsActiveFlowInfoOpen] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [supportRequestSubmitting, setSupportRequestSubmitting] = useState(false);
  const [reviewRequestItem, setReviewRequestItem] = useState(null);
  const [reviewRequestSubmitting, setReviewRequestSubmitting] = useState(false);
  const readingItemIdsRef = useRef(new Set());
  const scrollRootRef = useRef(null);

  const locale = i18n.resolvedLanguage || "en";
  const lang = locale.split("-")[0];
  const policyV3 = Number(data?.rules?.policy_version) >= 3;
  const copy = useMemo(() => {
    const base = { ...COPY.en, ...(COPY[lang] || {}) };
    return policyV3 ? { ...base, ...(COPY_V3_OVERRIDES[lang] || COPY_V3_OVERRIDES.en) } : base;
  }, [lang, policyV3]);
  const activeTab = mode === "tickets" ? "tickets" : "rating";
  const isTicketsMode = activeTab === "tickets";
  const isRulesScreen = screen === "rules" && !isTicketsMode;
  const pageTitle = isTicketsMode ? copy.ticketsTitle : copy.ratingTab;
  const currentPageTitle = isRulesScreen ? copy.rules : pageTitle;
  const effectiveScreen = isRulesScreen ? "rules" : "main";
  const scorePercent = Number(data?.score_percent) || 0;
  const premiumActive = Boolean(data?.premium_active);
  const premiumResetAvailable = Boolean(data?.premium_reset_available);
  const premiumResetLastUsedAt = data?.premium_reset_last_used_at;
  const supportRequest = data?.support_request || null;
  const showResetEntry = !premiumActive && (scorePercent <= 0 || (policyV3 && Boolean(data?.cooldown_active)));
  const showPremiumEntry = !premiumActive || showResetEntry;
  const timelineItems = useMemo(() => {
    const items = Array.isArray(data?.timeline) ? data.timeline : [];
    return mergeApprovedAttendanceReviewTimeline(
      items.map((item) => localizeTimelineItem(item, copy))
    );
  }, [copy, data?.timeline]);
  const unreadChangesCount = useMemo(() => {
    return timelineItems.reduce((count, item) => count + (item?.is_unread ? 1 : 0), 0);
  }, [timelineItems]);
  const ticketItems = useMemo(() => {
    return Array.isArray(data?.tickets) ? data.tickets : [];
  }, [data?.tickets]);
  const activeJoinItems = Array.isArray(data?.active_joins)
    ? data.active_joins
    : data?.active_slots;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: payload } = await api.get("/events/participant-rating/");
      setData(payload);
    } catch (err) {
      console.error("[ParticipantRatingPage] load failed", err);
      setError(copy.error);
    } finally {
      setLoading(false);
    }
  }, [copy.error]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const refresh = () => {
      void load();
    };
    window.addEventListener("vg-participant-rating-refresh", refresh);
    return () => window.removeEventListener("vg-participant-rating-refresh", refresh);
  }, [load]);

  useEffect(() => {
    const openRules = () => {
      if (!isTicketsMode) setScreen("rules");
    };
    window.addEventListener("vg-participant-rating-rules-open", openRules);
    return () => window.removeEventListener("vg-participant-rating-rules-open", openRules);
  }, [isTicketsMode]);

  useEffect(() => {
    const closeRules = () => {
      setScreen("main");
    };
    window.addEventListener("vg-participant-rating-rules-close", closeRules);
    return () => window.removeEventListener("vg-participant-rating-rules-close", closeRules);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const frameId = window.requestAnimationFrame(() => {
      scrollParticipantRatingViewToTop(scrollRootRef.current);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [effectiveScreen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const emitScreenState = () => {
      window.dispatchEvent(new CustomEvent("vg-participant-rating-screen", {
        detail: {
          screen: effectiveScreen,
          title: currentPageTitle,
          mode: activeTab,
          rulesAvailable: !isTicketsMode,
        },
      }));
    };

    emitScreenState();
    const frameId = window.requestAnimationFrame(emitScreenState);
    const timerId = window.setTimeout(emitScreenState, 80);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timerId);
      window.dispatchEvent(
        new CustomEvent("vg-participant-rating-screen", {
          detail: {
            screen: null,
            title: "",
            mode: null,
            rulesAvailable: false,
          },
        })
      );
    };
  }, [activeTab, currentPageTitle, effectiveScreen, isTicketsMode]);

  const timelineGroups = useMemo(() => {
    return groupTimelineByDate(timelineItems, locale);
  }, [timelineItems, locale]);

  const openTimelineEvent = useCallback((item) => {
    const eventId = Number(item?.event_id);
    if (!eventId) return;

    const preview = item?.event_preview || {};
    navigate(buildEventPath(eventId, preview?.name || item?.event_name || item?.title || ""));
  }, [navigate]);

  const openPremiumPage = useCallback(() => {
    navigate("/menu/subscription");
  }, [navigate]);

  const handlePremiumReset = useCallback(async () => {
    try {
      setResetSubmitting(true);
      setResetMessage("");
      await api.post("/events/participant-rating/reset/");
      setResetMessage(copy.resetSuccess);
      await load();
    } catch (err) {
      setResetMessage(err?.response?.data?.error || copy.error);
    } finally {
      setResetSubmitting(false);
    }
  }, [copy.error, copy.resetSuccess, load]);

  const handleSupportRequest = useCallback(async () => {
    try {
      setSupportRequestSubmitting(true);
      setResetMessage("");
      const response = await api.post("/events/participant-rating/support-request/", {
        source: "rating_page",
      });
      const nextRequest = response?.data?.support_request || null;
      setData((prev) => (prev ? { ...prev, support_request: nextRequest } : prev));
    } catch (err) {
      setResetMessage(err?.response?.data?.error || copy.error);
    } finally {
      setSupportRequestSubmitting(false);
    }
  }, [copy.error]);

  const openReviewRequestModal = useCallback((item) => {
    setReviewRequestItem(item);
  }, []);

  const closeReviewRequestModal = useCallback(() => {
    if (reviewRequestSubmitting) return;
    setReviewRequestItem(null);
  }, [reviewRequestSubmitting]);

  const submitAttendanceReviewRequest = useCallback(async () => {
    const eventId = Number(reviewRequestItem?.event_id);
    if (!eventId || reviewRequestSubmitting) return;

    try {
      setReviewRequestSubmitting(true);
      const response = await api.post(`/events/${eventId}/attendance-review-request/`, {
        source: "rating_page",
      });
      const reviewRequest = response?.data?.review_request || null;

      setData((prev) => {
        if (!prev?.timeline) return prev;
        return {
          ...prev,
          timeline: prev.timeline.map((timelineItem) => (
            timelineItem?.id === reviewRequestItem?.id
              ? {
                  ...timelineItem,
                  attendance_review: {
                    ...(timelineItem?.attendance_review || {}),
                    can_request: false,
                    ...(reviewRequest || {}),
                  },
                }
              : timelineItem
          )),
        };
      });

      setReviewRequestItem(null);
    } catch (err) {
      setResetMessage(err?.response?.data?.error || copy.error);
    } finally {
      setReviewRequestSubmitting(false);
    }
  }, [copy.error, reviewRequestItem, reviewRequestSubmitting]);

  const markTimelineItemRead = useCallback(async (item) => {
    const itemKey = item?.id;
    const logId = item?.log_id;
    const extraLogIds = Array.isArray(item?.extra_log_ids) ? item.extra_log_ids : [];
    const notificationId = item?.notification_id;
    if (!itemKey || !item?.is_unread || readingItemIdsRef.current.has(itemKey)) return;

    readingItemIdsRef.current.add(itemKey);
    try {
      await api.post("/events/participant-rating/read/", {
        log_ids: [
          ...new Set([logId, ...extraLogIds].filter(Boolean)),
        ],
        notification_ids: notificationId ? [notificationId] : [],
      });

      setData((prev) => {
        if (!prev?.timeline) return prev;
        const nextTimeline = prev.timeline.map((timelineItem) =>
          timelineItem?.id === itemKey
            ? { ...timelineItem, is_unread: false }
            : timelineItem
        );
        return {
          ...prev,
          timeline: nextTimeline,
        };
      });

      window.dispatchEvent(
        new CustomEvent("vg-participant-rating-unread-refresh")
      );
    } catch (err) {
      console.error("[ParticipantRatingPage] failed to mark item as read", err);
    } finally {
      readingItemIdsRef.current.delete(itemKey);
    }
  }, []);

  const handleClosePage = useCallback(() => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }
    navigate(-1);
  }, [navigate, onClose]);

  const handleHeaderBack = useCallback(() => {
    if (isRulesScreen) {
      setScreen("main");
      return;
    }
    handleClosePage();
  }, [handleClosePage, isRulesScreen]);

  const openRulesScreen = useCallback(() => {
    if (isTicketsMode) return;
    setScreen("rules");
  }, [isTicketsMode]);

  const rulesPortalTarget = typeof document !== "undefined"
    ? document.getElementById("app-workspace") || document.body
    : null;
  const rulesButtonPosition = rulesPortalTarget?.id === "app-workspace" ? "absolute" : "fixed";

  return (
    <div
      className={`relative flex min-h-0 w-full flex-col overflow-hidden text-white ${
        embedded ? "h-auto min-h-full" : "h-full"
      }`}
      style={embedded ? undefined : AUTH_GRADIENT_STYLE}
    >
      <div ref={scrollRootRef} className={embedded ? "flex-1 overflow-visible" : "flex-1 overflow-y-auto"}>
        <div className={`mx-auto w-full max-w-[760px] ${
          embedded
            ? "px-0 pb-[calc(var(--bottom-nav-clearance,112px)+24px)] pt-0"
            : "px-[var(--vg-page-gutter)] pb-[calc(var(--bottom-nav-clearance,112px)+24px)] pt-[calc(var(--vg-safe-area-top)+20px)] sm:px-6 lg:px-8 lg:pb-24 lg:pt-5"
        }`}>
          {!embedded ? (
          <div className="relative mb-3">
            {!isRulesScreen && !isTicketsMode && unreadChangesCount > 0 && (
              <span className="absolute left-0 top-1/2 inline-flex h-[18px] min-w-[18px] -translate-y-1/2 items-center justify-center rounded-full bg-white/80 px-[6px] text-[12px] font-semibold leading-none text-blue-600 shadow-[0_8px_18px_rgba(0,0,0,0.22)]">
                {unreadChangesCount}
              </span>
            )}
            <h1 className="mx-auto max-w-[calc(100%-48px)] text-center text-2xl font-semibold leading-tight text-white sm:text-3xl">
              {currentPageTitle}
            </h1>
            <ParticipantRatingCloseButton
              onClick={handleHeaderBack}
              label={copy.closeAlt}
              className="absolute left-0 top-1/2 -translate-y-1/2"
            />
          </div>
          ) : null}

          {isRulesScreen ? (
            <RulesScreen
              copy={copy}
              rules={data?.rules}
              lang={lang}
              embedded={embedded}
            />
          ) : loading ? (
            isTicketsMode ? <ParticipantTicketsSkeleton /> : <ParticipantRatingSkeleton />
          ) : error ? (
            <div className="rounded-3xl border border-rose-400/20 bg-rose-500/10 p-6">
              <p className="text-sm text-rose-100">{error}</p>
              <button
                type="button"
                onClick={load}
                className="mt-4 inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white"
              >
                {copy.retry}
              </button>
            </div>
          ) : (
            activeTab === "tickets" ? (
              <MyTicketsSection
                copy={copy}
                items={ticketItems}
                locale={locale}
                onOpen={openTimelineEvent}
                showTitle={!isTicketsMode}
              />
            ) : (
            <>
              <section className="-mt-1 pt-0">
                <RatingGauge score={data?.score_percent} />
                {!showResetEntry && (
                  <SlotsSummary
                    copy={copy}
                    total={data?.slots_total ?? 0}
                    used={Number(data?.slots_used) || 0}
                    left={data?.slots_left ?? 0}
                    unlimited={Boolean(data?.slots_unlimited)}
                  />
                )}
                {!showResetEntry && Array.isArray(activeJoinItems) && activeJoinItems.length > 0 && (
                  <ActiveSlotsSection
                    copy={copy}
                    items={activeJoinItems}
                    onOpen={openTimelineEvent}
                    onOpenInfo={() => setIsActiveFlowInfoOpen(true)}
                  />
                )}
                {resetMessage && (
                  <div className="mt-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-center text-sm text-emerald-100/90">
                    {resetMessage}
                  </div>
                )}
                {showResetEntry ? (
                  <ZeroRatingPanel
                    copy={copy}
                    scorePercent={scorePercent}
                    cooldownActive={Boolean(data?.cooldown_active)}
                    zeroRecoveryAt={data?.zero_recovery_at}
                    supportRequest={supportRequest}
                    supportRequestSubmitting={supportRequestSubmitting}
                    onSupportRequest={handleSupportRequest}
                    premiumActive={premiumActive}
                    premiumResetAvailable={premiumResetAvailable}
                    premiumResetLastUsedAt={premiumResetLastUsedAt}
                    resetSubmitting={resetSubmitting}
                    onPremiumReset={handlePremiumReset}
                    onPremiumPage={openPremiumPage}
                    locale={locale}
                  />
                ) : null}
                {premiumActive && premiumResetAvailable ? (
                  <div className="mt-3 rounded-[28px] border border-emerald-400/20 bg-[#13233b] p-5 text-center">
                    <p className="text-sm leading-6 text-white/70">{copy.resetAvailableDescription}</p>
                    <button type="button" onClick={handlePremiumReset} disabled={resetSubmitting}
                      className="mt-4 inline-flex h-12 min-w-[190px] items-center justify-center rounded-full bg-emerald-500 px-6 font-semibold text-white disabled:opacity-60">
                      {resetSubmitting ? copy.resetLoading : copy.resetCta}
                    </button>
                  </div>
                ) : null}
                {!showResetEntry && showPremiumEntry && (
                  <div className="mt-3">
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={openPremiumPage}
                        className="inline-flex h-12 min-w-[240px] items-center justify-center overflow-hidden rounded-2xl bg-[linear-gradient(90deg,#2563eb_0%,#0d8cf2_55%,#22d3ee_100%)] px-6 text-base font-semibold text-white shadow-[0_14px_28px_rgba(37,99,235,0.22)]"
                      >
                        {copy.unlockEntry}
                      </button>
                    </div>
                  </div>
                )}
              </section>

              <section className="mt-3">
                <h2 className="mb-4 text-lg font-semibold text-white">{copy.historyTitle}</h2>
                {timelineGroups.length === 0 ? (
                  <div className="rounded-3xl border border-white/10 bg-[#0f1d34] p-5 text-sm text-white/60">
                    {copy.historyEmpty}
                  </div>
                ) : (
                  <div className="space-y-5 pb-2">
                    {timelineGroups.map((group) => (
                      <div key={group.dateLabel}>
                        <div className="mb-2 text-[15px] font-semibold text-white/95">{group.dateLabel}</div>
                        <div className="space-y-4">
                          {group.items.map((item) => (
                            <TimelineObservedCard
                              key={item.id}
                              item={item}
                              locale={locale}
                              onOpen={openTimelineEvent}
                              onViewed={markTimelineItemRead}
                              copy={copy}
                              onRequestReview={openReviewRequestModal}
                              requestSubmittingId={reviewRequestSubmitting ? reviewRequestItem?.id : null}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

            </>
            )
          )}
        </div>
      </div>

      {!isRulesScreen && !isTicketsMode && rulesPortalTarget ? createPortal(
        <button
          type="button"
          onClick={openRulesScreen}
          className={`${rulesButtonPosition} bottom-[max(24px,env(safe-area-inset-bottom))] left-1/2 z-30 hidden h-12 min-w-[132px] -translate-x-1/2 items-center justify-center rounded-full bg-blue-500 px-6 text-lg font-semibold text-white shadow-[0_18px_36px_rgba(0,115,255,0.35)] lg:inline-flex`}
        >
          {copy.rules}
        </button>,
        rulesPortalTarget
      ) : null}

      <ActiveFlowInfoModal
        copy={copy}
        open={isActiveFlowInfoOpen}
        onClose={() => setIsActiveFlowInfoOpen(false)}
      />

      <AttendanceReviewRequestModal
        copy={copy}
        item={reviewRequestItem}
        submitting={reviewRequestSubmitting}
        onClose={closeReviewRequestModal}
        onSubmit={submitAttendanceReviewRequest}
      />

    </div>
  );
}
